import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { postJournal, voidJournal } from '../../api/journal';
import { ACCOUNTS, rateFor, getSupplierDepositRemaining, costPayableAccount, supplierTypesFor } from '../../api/accounts';
import { applyQuotationDepositToInvoice } from '../../api/receiptAllocations';
import TypeSearch from '../../components/common/TypeSearch';
import SupplierPicker from '../../components/common/SupplierPicker';
import BranchSelectModal from '../../components/common/BranchSelectModal';
import { usePasscode } from '../../components/common/PasscodeModal';

const COST_ACCOUNTS = {
  ticket:  { expense: ACCOUNTS.TICKET_COST_LKR, payable: ACCOUNTS.AIRLINE_PAYABLE },
  visa:    { expense: ACCOUNTS.VISA_COST,         payable: ACCOUNTS.VISA_PAYABLE },
  package: { expense: ACCOUNTS.HOTEL_LAND_COST,  payable: ACCOUNTS.HOTEL_LAND_PAYABLE },
};
const REVENUE_ACCOUNTS = {
  ticket:  { revenue: ACCOUNTS.TICKET_REVENUE,  refType: 'INVOICE_TICKET' },
  visa:    { revenue: ACCOUNTS.VISA_REVENUE,     refType: 'INVOICE_VISA' },
  package: { revenue: ACCOUNTS.BOOKING_REVENUE,  refType: 'INVOICE_BOOKING' },
};
const PRODUCT_TYPE_MAP = { ticket: 'Ticket', visa: 'Visa', package: 'Package' };
// commissions.reference_type has a DB check constraint allowing only these three values
// (not 'INVOICE' — that was silently violating the constraint on every insert).
const COMMISSION_REF_TYPE = { ticket: 'TICKET', visa: 'VISA', package: 'BOOKING' };
// Reverse of COMMISSION_REF_TYPE — for package/FIT invoices that bundle a package cost with
// e.g. a visa cost, a single invoice can carry commission lines of different product types.
const ITEM_TYPE_FROM_REF = { TICKET: 'ticket', VISA: 'visa', BOOKING: 'package' };
// Real passenger categories (not the cost-only "Common (All Pax)" entries) — sourced live
// from the pax_types table (admin-manageable from PackageDetail.js) so a new category
// shows up here automatically, matching what a package's own rates can be entered against.
function usePaxTypes() {
  const [paxTypes, setPaxTypes] = useState([]);
  useEffect(() => {
    supabase.from('pax_types').select('name, is_common').eq('is_active', true).eq('is_common', false).order('sort_order')
      .then(({ data }) => setPaxTypes((data || []).map(t => t.name)));
  }, []);
  return paxTypes;
}

function emptySupplier() {
  // line_item_index: '' means "Shared" — this cost splits evenly across every passenger on
  // the invoice (the only behavior that existed before per-passenger assignment was added).
  // Set to a specific revenue-line index to attribute 100% of this cost to that one passenger
  // instead (e.g. each passenger's own embassy/visa fees on a multi-pax visa invoice).
  // pax_type_filter: '' means no type restriction. Set to e.g. 'Child With Bed' to attribute
  // this cost across every line item of that type instead of one specific line or everyone —
  // this is what a package's per-pax-type supplier rate (Adult/Child With Bed/Child Without
  // Bed/Infant) pulls into automatically, since a package can have several pax of the same
  // type spread across different line items.
  return { supplier_id: '', supplier_label: '', cost_amount: '', currency_id: '', exchange_rate: '1', notes: '', per_pax: false, cost_type: 'supplier', journaled: false, line_item_index: '', pax_type_filter: '' };
}
// Compact per-passenger label for the "Applies To" dropdown — takes the name portion before
// the first " — " (revenue-line descriptions read "NAME — Visa Processing — COUNTRY" or
// "NAME — Travel Package"; the create-invoice path's lines just have a plain type like
// "Adult", which passes through unchanged since there's no " — " to split on).
function paxLabel(desc, i) {
  const name = (desc || '').split(' — ')[0].trim();
  return name ? `${i + 1}. ${name}` : `Passenger ${i + 1}`;
}
let ticketSegmentSeq = 0;
function emptyTicketSegment() {
  return { key: `seg-${++ticketSegmentSeq}`, airline: '', pnr: '', route_from: '', route_to: '' };
}

let commissionLineSeq = 0;
function emptyCommissionLine(agentId = '', agentLabel = '', units = 1, productType = '') {
  return {
    key: `new-${++commissionLineSeq}`,
    id: null,
    agent_id: agentId,
    agent_label: agentLabel,
    // Only meaningful for package/FIT invoices, which can bundle e.g. a package cost and a
    // visa cost on one invoice — blank means "use the invoice's own type" (ticket/visa
    // invoices always do this; only package invoices let a line override it).
    product_type: productType,
    rule_name: '',
    commission_table_id: '',
    commission_percent: '',
    commission_amount: '',
    // How many units (visas, tickets, pax…) this line's commission covers — defaults to
    // the invoice's full count, but staff can split it when multiple agents share credit
    // for one invoice (e.g. 2 of 4 visas each).
    units: units || 1,
    include: true,
    locked: false,
  };
}
function today() { return new Date().toISOString().slice(0, 10); }
function fmt(n) { return (n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

// ─── Costing-only mode ─────────────────────────────────────────────────────────

function buildLineItemsFromSource(type, data) {
  if (!data) return [];
  if (type === 'booking') {
    const numPax = parseInt(data.num_pax) || 1;
    const sellPerPax = parseFloat(data.total_sell || 0) / numPax;
    return [{ description: `${data.packages?.package_name || 'Package'} — Travel Package`, qty: numPax, unitPrice: sellPerPax, amount: sellPerPax * numPax }];
  }
  if (type === 'ticket') {
    const sell = parseFloat(data.sell_price || 0);
    return [{ description: `Flight Ticket${data.airline ? ` — ${data.airline}` : ''}`, qty: 1, unitPrice: sell, amount: sell }];
  }
  if (type === 'visa') {
    const sell = parseFloat(data.sell_price || 0);
    return [{ description: `Visa — ${data.country || ''}`, qty: 1, unitPrice: sell, amount: sell }];
  }
  return [];
}

export function CostingEntry({ invoiceId }) {
  const navigate = useNavigate();
  const { user, branchId } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const PAX_TYPES = usePaxTypes();
  const [invoice, setInvoice] = useState(null);
  const [resolvedLineItems, setResolvedLineItems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [lkrId, setLkrId] = useState('');
  const [suppliers, setSuppliers] = useState([emptySupplier()]);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState('');
  const { prompt: promptPasscode, PasscodeModal } = usePasscode();

  // commission — a repeatable list, since one agent can earn more than one rule at once
  // (e.g. a flat per-visa amount PLUS a separate GP-tiered cut)
  const [commissionLines, setCommissionLines] = useState([]);
  // Keyed by product type ('ticket'/'visa'/'package') — see lineItemType() below.
  const [availableRuleNames, setAvailableRuleNames] = useState({ ticket: [], visa: [], package: [] });
  // True once the rule-names fetch for the current product type has resolved. Without this,
  // the auto-lookup below can run before rule names arrive and misread "still loading" as
  // "no rule required", locking onto the first FIXED rule regardless of what should apply.
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [defaultAgentId, setDefaultAgentId] = useState('');
  const [defaultAgentLabel, setDefaultAgentLabel] = useState('');
  // Already-saved commission lines are read-only until unlocked with the admin
  // passcode — editing or removing a real commission record needs sign-off.
  const [unlockedCommissionKeys, setUnlockedCommissionKeys] = useState([]);

  useEffect(() => {
    supabase.from('currencies').select('id, code').order('code').then(({ data }) => {
      setCurrencies(data || []);
      const lkr = (data || []).find(c => c.code === 'LKR');
      if (lkr) setLkrId(lkr.id);
    });
    supabase.from('invoices')
      .select('*, clients(full_name, client_code, care_of_agent_id, agents:care_of_agent_id(id, tls_code, full_name))')
      .eq('id', invoiceId).single()
      .then(async ({ data: inv }) => {
        if (!inv) return;
        setInvoice(inv);
        if (inv.cost_items?.length) {
          setSuppliers(inv.cost_items.map(c => ({
            supplier_id: c.supplier_id || '',
            supplier_label: c.supplier_label || '',
            sub_supplier_id: c.sub_supplier_id || null, sub_supplier_label: c.sub_supplier_label || null,
            cost_amount: String(c.cost_amount || ''),
            currency_id: c.currency_id || '',
            exchange_rate: String(c.exchange_rate || '1'),
            notes: c.notes || '',
            per_pax: !!c.per_pax,
            cost_type: c.cost_type || 'supplier',
            journaled: !!c.journaled,
            line_item_index: c.line_item_index != null ? String(c.line_item_index) : '',
            pax_type_filter: c.pax_type_filter || '',
          })));
        }
        // Resolve line items — if null, fetch from source
        let resolvedLines = [];
        if (inv.line_items?.length) {
          resolvedLines = inv.line_items;
          setResolvedLineItems(inv.line_items);
        } else if (inv.source_id && inv.source_type) {
          const srcTable = inv.source_type === 'booking' ? 'bookings' : inv.source_type === 'ticket' ? 'tickets' : 'visas';
          const selectFields = inv.source_type === 'booking'
            ? '*, packages(package_name, package_code)'
            : '*';
          const { data: src } = await supabase.from(srcTable).select(selectFields).eq('id', inv.source_id).single();
          resolvedLines = buildLineItemsFromSource(inv.source_type, src);
          setResolvedLineItems(resolvedLines);
        }
        const paxCount = resolvedLines.reduce((s, l) => s + (parseInt(l.qty) || 0), 0) || 1;

        const agent = inv.clients?.agents;
        if (agent) { setDefaultAgentId(agent.id); setDefaultAgentLabel(`${agent.tls_code} — ${agent.full_name}`); }

        // Existing commissions for this invoice, if any were already recorded (e.g. at
        // creation time, or a prior costing visit) — load them instead of starting blank.
        // Package/FIT invoices can bundle a package cost with e.g. a visa cost, so they may
        // carry commission rows of more than one reference_type on the same invoice — fetch
        // all three and let each line show its own saved product type.
        const itemType = inv.source_type === 'booking' ? 'package' : (inv.source_type || 'ticket');
        const commRefTypes = itemType === 'package' ? ['BOOKING', 'VISA', 'TICKET'] : [COMMISSION_REF_TYPE[itemType]];
        const { data: existingComms } = await supabase.from('commissions')
          .select('*, agents:agent_ref_id(id, tls_code, full_name), commission_tables:commission_table_id(rule_name)')
          .in('reference_type', commRefTypes).eq('reference_id', invoiceId);
        if (existingComms?.length) {
          setCommissionLines(existingComms.map(c => ({
            key: c.id,
            id: c.id,
            agent_id: c.agent_ref_id || '',
            agent_label: c.agents ? `${c.agents.tls_code} — ${c.agents.full_name}` : '',
            product_type: itemType === 'package' ? (ITEM_TYPE_FROM_REF[c.reference_type] || 'package') : '',
            rule_name: c.commission_tables?.rule_name || '',
            commission_table_id: c.commission_table_id || '',
            commission_percent: c.commission_percent != null ? String(c.commission_percent) : '',
            commission_amount: c.commission_amount != null ? String(c.commission_amount) : '',
            units: c.units != null ? String(c.units) : String(paxCount),
            include: true,
            locked: c.commission_status !== 'PENDING',
          })));
        } else if (agent) {
          setCommissionLines([emptyCommissionLine(agent.id, `${agent.tls_code} — ${agent.full_name}`, paxCount)]);
        }
      });
  }, [invoiceId]);

  // A line's pax type — its own `type` if set, else the leading "Adult — …" of its
  // description (how quotation/booking-sourced lines record it), so type-filtered costs
  // still resolve on invoices saved before `type` was stored on the line.
  const lineType = (l) => l.type || PAX_TYPES.find(t => (l.description || '').split(' — ')[0].trim() === t) || '';
  const [lineTypesDirty, setLineTypesDirty] = useState(false);
  // Changing a passenger's type is a description/attribution change only — amounts are
  // untouched, so no journal is posted; it's saved alongside the costing.
  const setLineType = (i, t) => {
    setResolvedLineItems(items => items.map((l, idx) => {
      if (idx !== i) return l;
      const old = lineType(l);
      const desc = l.description || '';
      const description = old && desc.startsWith(old) ? t + desc.slice(old.length) : (desc ? `${t} — ${desc}` : t);
      return { ...l, type: t, description };
    }));
    setLineTypesDirty(true);
  };

  const addSupplier = () => setSuppliers(s => [...s, emptySupplier()]);
  const removeSupplier = (i) => setSuppliers(s => s.filter((_, idx) => idx !== i));
  const updateSupplier = (i, fields) => setSuppliers(s => s.map((r, idx) => idx === i ? { ...r, ...fields } : r));

  const totalSell = resolvedLineItems.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const numPax = resolvedLineItems.reduce((s, l) => s + (parseInt(l.qty) || 0), 0) || 1;
  // A cost row assigned to one specific passenger (line_item_index set) scales by that
  // passenger's own qty when per_pax, or is a flat total when not — never by the invoice's
  // full pax count, since it doesn't apply to anyone else.
  const assignedQty = (c) => {
    const item = resolvedLineItems[parseInt(c.line_item_index)];
    return item ? (parseInt(item.qty) || 1) : 1;
  };
  const isAssigned = (c) => c.line_item_index !== '' && c.line_item_index != null;
  // See the matching pax_type_filter comment on the new-invoice path below — a package's
  // per-pax-type rate applies across every line item of that type, not one specific line.
  const isTypeFiltered = (c) => !!c.pax_type_filter;
  const typeMatchQty = (c) => resolvedLineItems.reduce((s, it) => lineType(it) === c.pax_type_filter ? s + (parseInt(it.qty) || 1) : s, 0);
  // How many pax a per_pax cost row is multiplied by when it is POSTED — must match the
  // on-screen total below: a pax-type row counts only that type's passengers (0 if there
  // are none, e.g. an Adult rate on a child-only invoice), an assigned row only its own
  // passenger, a shared row everyone. Posting per_pax × total pax for every row put
  // Adult rates into the GL on child-only invoices.
  const postQty = (c) => isTypeFiltered(c) ? typeMatchQty(c) : isAssigned(c) ? assignedQty(c) : numPax;
  const totalCostLKR = suppliers.reduce((s, c) => {
    const base = (parseFloat(c.cost_amount) || 0) * (parseFloat(c.exchange_rate) || 1);
    if (isTypeFiltered(c)) return s + (c.per_pax ? base * typeMatchQty(c) : base);
    if (isAssigned(c)) return s + (c.per_pax ? base * assignedQty(c) : base);
    return s + (c.per_pax ? base * numPax : base);
  }, 0);
  const gpLKR = totalSell - totalCostLKR;

  // Per-passenger margin — each revenue line's own sell amount minus its own share of cost,
  // not the invoice's blended total. A cost row explicitly assigned to one passenger (via
  // "Applies To") attributes 100% of itself to that passenger and 0 to everyone else — e.g.
  // each passenger's own distinct embassy/visa fees. A row left "Shared" splits evenly across
  // all pax, same as before assignment existed (a per_pax rate applies per person; a flat
  // total divides evenly). Used so GP-tiered commissions apply the tier to each passenger's
  // own margin — two passengers who land in different tiers each earn their own tier's rate.
  const perPassengerMargins = resolvedLineItems.map((item, itemIdx) => {
    const qty = parseInt(item.qty) || 1;
    const sell = parseFloat(item.amount) || 0;
    const costShare = suppliers.reduce((s, c) => {
      const base = (parseFloat(c.cost_amount) || 0) * (parseFloat(c.exchange_rate) || 1);
      if (isTypeFiltered(c)) {
        if (lineType(item) !== c.pax_type_filter) return s;
        const matchQty = typeMatchQty(c);
        return s + (c.per_pax ? base * qty : (matchQty > 0 ? (base * qty) / matchQty : 0));
      }
      if (isAssigned(c)) {
        if (parseInt(c.line_item_index) !== itemIdx) return s;
        return s + (c.per_pax ? base * qty : base);
      }
      return s + (c.per_pax ? base * qty : (numPax > 0 ? (base * qty) / numPax : 0));
    }, 0);
    // description/sell/costShare are carried for the per-pax GP display only — the
    // commission lookup above only ever reads .margin.
    return { description: item.description, qty, sell, costShare, margin: sell - costShare };
  });

  const commissionItemType = invoice ? (invoice.source_type === 'booking' ? 'package' : (invoice.source_type || 'ticket')) : 'ticket';
  const commissionProductType = PRODUCT_TYPE_MAP[commissionItemType] || 'Ticket';
  const totalCommission = commissionLines.reduce((s, l) => s + (l.include ? (parseFloat(l.commission_amount) || 0) : 0), 0);
  // Each passenger's own share of commission, summed across every included GP-tiered
  // commission line's per-passenger breakdown — drives the Commission/GP columns in the
  // per-passenger table below. Flat/count-based lines aren't tied to any one passenger's
  // margin, so they don't contribute here (only to the cumulative Commission figure).
  const perPaxCommission = perPassengerMargins.map((_, i) =>
    commissionLines.reduce((s, l) => s + (l.include && l._perPax && l._perPax[i] != null ? l._perPax[i] : 0), 0)
  );

  const addCommissionLine = () => setCommissionLines(ls => [...ls, emptyCommissionLine(defaultAgentId, defaultAgentLabel, numPax)]);
  const updateCommissionLine = (key, fields) => setCommissionLines(ls => ls.map(l => l.key === key ? { ...l, ...fields } : l));

  // Removing a brand-new (never-saved) line needs no sign-off — nothing's at risk yet.
  // Removing an already-saved commission record requires the admin passcode.
  const removeCommissionLine = async (line) => {
    if (line.id) {
      try { await promptPasscode(); } catch { return; }
    }
    setCommissionLines(ls => ls.filter(l => l.key !== line.key));
  };

  // Already-saved commission lines render read-only until unlocked here with the
  // admin passcode — a deliberate, logged action rather than a stray edit.
  const unlockCommissionLine = async (key) => {
    try { await promptPasscode(); } catch { return; }
    setUnlockedCommissionKeys(ks => [...ks, key]);
  };

  // What product type a given commission line should use — only package/FIT invoices let a
  // line override the invoice's own type (e.g. a visa-commission line on a package invoice).
  const lineItemType = (line) => (commissionItemType === 'package' ? (line.product_type || 'package') : commissionItemType);

  // Rule names per product type, fetched for all three so a package/FIT invoice can offer
  // any type's rules on a per-line basis. Ticket/visa invoices only ever use their own single
  // type, but fetching all three up front is cheap and keeps this independent of per-line state.
  useEffect(() => {
    setRulesLoaded(false);
    supabase.from('commission_tables')
      .select('rule_name, product_type')
      .eq('is_active', true)
      .then(({ data }) => {
        const map = { ticket: [], visa: [], package: [] };
        (data || []).forEach(r => {
          const key = Object.keys(PRODUCT_TYPE_MAP).find(k => PRODUCT_TYPE_MAP[k] === r.product_type);
          if (key && r.rule_name && !map[key].includes(r.rule_name)) map[key].push(r.rule_name);
        });
        setAvailableRuleNames(map);
        setRulesLoaded(true);
      });
  }, []);

  // Auto-lookup each non-locked line's commission whenever its agent/rule/product type or the GP changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const updated = await Promise.all(commissionLines.map(async (line) => {
        if (line.locked) return line;
        if (!line.agent_id) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
        const lineProductType = PRODUCT_TYPE_MAP[lineItemType(line)] || 'Ticket';

        // Always fetch every active rule for this product type ourselves — deciding
        // ambiguity from this same query's result, not from the separately-fetched
        // availableRuleNames state, which can lag behind and cause this lookup to
        // silently guess a rule (e.g. picking another agent's per-visa rate) before
        // that other fetch has resolved.
        const { data: allRows } = await supabase.from('commission_tables').select('*').eq('product_type', lineProductType).eq('is_active', true).limit(200);
        if (!allRows?.length) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
        const distinctRuleNames = [...new Set(allRows.map(r => r.rule_name).filter(Boolean))];
        if (!line.rule_name && distinctRuleNames.length > 1) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
        const rows = line.rule_name ? allRows.filter(r => r.rule_name === line.rule_name) : allRows;
        if (!rows.length) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };

        const fixedRule = rows.find(r => r.commission_type === 'FIXED');
        if (fixedRule) return { ...line, commission_table_id: fixedRule.id, commission_amount: String(parseFloat(fixedRule.fixed_amount) || 0), commission_percent: '' };

        // COUNT_BASED — a per-unit rate (e.g. per visa), optionally tiered by unit-count
        // range (gp_from/gp_to reused as unit bounds, same pattern as GP tiers). Multiplies
        // by this line's own `units`, not the invoice's GP.
        const countRows = rows.filter(r => r.commission_type === 'COUNT_BASED');
        if (countRows.length) {
          const units = parseFloat(line.units) || 0;
          const tier = countRows.find(r => parseFloat(r.gp_from) <= units && (r.gp_to === null || parseFloat(r.gp_to) >= units)) || countRows[0];
          if (!tier || units <= 0) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
          return { ...line, commission_table_id: tier.id, commission_amount: String((units * (parseFloat(tier.amount_per_unit) || 0)).toFixed(2)), commission_percent: '' };
        }

        // GP-tiered: apply the rule to each passenger's own margin and sum — a two-pax
        // invoice where one passenger's margin lands in a different tier from the other's
        // gets each passenger commissioned at their own tier's rate, not the invoice's
        // blended total. Falls back to the invoice-wide margin when there's only one
        // revenue line (the common case), which behaves identically to the old lookup.
        const marginsToTier = perPassengerMargins.length ? perPassengerMargins : [{ qty: numPax, margin: gpLKR }];
        // Per-passenger breakdown of this line's own commission, aligned index-for-index with
        // perPassengerMargins (only meaningful when marginsToTier IS perPassengerMargins — the
        // single-line gpLKR fallback has no per-passenger table to align to). Read by the
        // per-passenger display table below to show each passenger's own commission and net GP.
        const perPax = perPassengerMargins.length ? perPassengerMargins.map(() => 0) : null;
        let total = 0, anyTierMatched = false;
        marginsToTier.forEach(({ margin }, idx) => {
          if (margin <= 0) return;
          const tier = rows.find(r => parseFloat(r.gp_from) <= margin && (r.gp_to === null || parseFloat(r.gp_to) >= margin));
          if (!tier) return;
          anyTierMatched = true;
          let amt = 0;
          if (parseFloat(tier.fixed_amount) > 0) amt = parseFloat(tier.fixed_amount);
          else if (parseFloat(tier.commission_percent) > 0) amt = margin * parseFloat(tier.commission_percent) / 100;
          total += amt;
          if (perPax) perPax[idx] = amt;
        });
        if (!anyTierMatched) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
        // commission_table_id/percent aren't set to one value here — different passengers can
        // land in different tiers, so there's no single rate/table id representing the line.
        return { ...line, commission_amount: total.toFixed(2), commission_percent: '', commission_table_id: '', _perPax: perPax };
      }));
      if (!cancelled) setCommissionLines(updated);
    })();
    return () => { cancelled = true; };
    // Only re-run when the inputs that affect the lookup change, not every time commissionLines
    // itself changes (which would loop, since this effect writes to it).
  }, [commissionLines.map(l => `${l.agent_id}|${l.rule_name}|${l.units}|${l.locked}|${l.product_type}`).join(','), perPassengerMargins.map(m => m.margin.toFixed(2)).join(','), gpLKR, commissionItemType, rulesLoaded]); // eslint-disable-line

  // Persists the current supplier rows and posts journals for anything genuinely new
  // (costs can arrive in stages — rows already saved on a prior visit are never re-posted).
  // Does not touch costing_finalized or navigate; callers decide what happens next.
  // willFinalize: true when called from finalizeCosting(), just before costing_finalized
  // flips to true — lets any Internal/Tour Operator cost rows post their deferred journal
  // in this same pass, alongside brand-new rows.
  async function persistCosting(willFinalize = false) {
    const validRows = suppliers.filter(s => (s.cost_type === 'internal' ? s.supplier_label : s.supplier_id) && s.cost_amount);
    if (!validRows.length) {
      throw new Error('Add at least one supplier or internal cost with an amount.');
    }

    const sig = (c) => `${c.cost_type || 'supplier'}|${c.supplier_id}|${c.supplier_label}|${c.cost_amount}|${c.currency_id}|${c.exchange_rate}|${c.notes}|${c.per_pax}`;
    const prevItems = invoice.cost_items || [];
    const prevBySig = new Map(prevItems.map(c => [sig(c), c]));
    const costItems = validRows.map(s => {
      const base = {
        supplier_id: s.cost_type === 'internal' ? null : s.supplier_id,
        supplier_label: s.supplier_label,
        sub_supplier_id: s.sub_supplier_id || null, sub_supplier_label: s.sub_supplier_label || null,
        cost_amount: parseFloat(s.cost_amount),
        currency_id: s.currency_id || lkrId,
        exchange_rate: parseFloat(s.exchange_rate) || 1,
        notes: s.notes,
        per_pax: !!s.per_pax,
        cost_type: s.cost_type || 'supplier',
        // Deliberately excluded from sig() — reassigning which passenger a cost belongs to
        // is a display/reporting change only, not a GL change, so it must never trigger a
        // reversal-and-repost of an already-journaled row.
        line_item_index: s.line_item_index !== '' && s.line_item_index != null ? parseInt(s.line_item_index) : null,
        pax_type_filter: s.pax_type_filter || '',
      };
      const match = prevBySig.get(sig(base));
      return { ...base, journaled: match?.journaled || false, journal_entry_id: match?.journal_entry_id || null };
    });

    // A posted row whose ONLY change is the supplier (e.g. an airline ticket reassigned to
    // IATA) is a payable moving between suppliers, not a new cost. Pair it with its old
    // row and transfer the AP (DR AP old supplier / CR AP new supplier) further down,
    // instead of posting the cost a second time — legacy rows have no journal_entry_id to
    // void, so a fresh post would leave the cost on both suppliers' ledgers.
    const costSig = (c) => `${c.cost_type || 'supplier'}|${c.cost_amount}|${c.currency_id}|${c.exchange_rate}|${c.notes}|${c.per_pax}`;
    const currentSigsBefore = new Set(costItems.map(sig));
    const orphanedPrev = prevItems.filter(p => p.journaled && (p.cost_type || 'supplier') === 'supplier' && !currentSigsBefore.has(sig(p)));
    const reassignments = [];
    for (const row of costItems) {
      if (row.journaled || row.cost_type !== 'supplier') continue;
      const pi = orphanedPrev.findIndex(p => costSig(p) === costSig(row) && p.supplier_id && p.supplier_id !== row.supplier_id);
      if (pi < 0) continue;
      const [prev] = orphanedPrev.splice(pi, 1);
      row.journaled = true;
      row.journal_entry_id = prev.journal_entry_id || null;
      reassignments.push({ from: prev, to: row });
    }

    // A previously-journaled row that no longer has a matching signature in the current
    // array was edited (its amount/currency/etc changed) or deleted outright — either way,
    // its old posting is now stale and would sit alongside a fresh one forever if left
    // alone. Reverse it before posting anything new, using the journal_entry_id captured
    // when it was first posted (older rows saved before this field existed won't have one
    // and can't be auto-reversed this way — those need a manual correction).
    // Reassigned rows are excluded — their posting stays and is transferred instead.
    const currentSigs = new Set(costItems.map(sig));
    const reassignedPrev = new Set(reassignments.map(r => r.from));
    for (const prev of prevItems) {
      if (prev.journaled && prev.journal_entry_id && !currentSigs.has(sig(prev)) && !reassignedPrev.has(prev)) {
        await voidJournal(prev.journal_entry_id, 'Cost row edited or removed on invoice re-save', username);
      }
    }

    // Keep booking.total_cost and gross_profit in sync when invoice is linked to a booking
    if (invoice.source_type === 'booking' && invoice.source_id) {
      await supabase.from('bookings').update({
        total_cost: totalCostLKR,
        gross_profit: totalSell - totalCostLKR,
      }).eq('id', invoice.source_id);
    }

    const sourceType = invoice.source_type || 'ticket';
    const typeKey = sourceType === 'booking' ? 'package' : sourceType;
    const { expense, payable } = COST_ACCOUNTS[typeKey] || COST_ACCOUNTS.ticket;
    const { refType } = REVENUE_ACCOUNTS[typeKey] || REVENUE_ACCOUNTS.ticket;
    // A brand-new invoice is never finalized yet, so Internal cost rows entered at
    // creation time defer their journal the same way a Costing Pending row would.
    const isFinalized = !!invoice.costing_finalized || willFinalize;
    // AP account follows each supplier's own type (an airline on a package invoice is
    // Airline Ticket Payable), the same account its payments clear.
    const supTypes = await supplierTypesFor([...costItems.map(c => c.supplier_id), ...reassignments.flatMap(r => [r.from.supplier_id, r.to.supplier_id])]);
    const payableFor = (sid) => costPayableAccount(supTypes[sid], payable);

    for (const { from, to } of reassignments) {
      const amt = from.per_pax ? from.cost_amount * postQty(from) : from.cost_amount;
      if (!amt) continue;
      const fromCurr = currencies.find(c => c.id === from.currency_id);
      const desc = `${invoice.invoice_number} — supplier reassigned: ${from.supplier_label} → ${to.supplier_label}`;
      await postJournal({
        date: invoice.issued_date || today(),
        description: desc,
        reference_type: refType,
        reference_id: invoiceId,
        currency_id: from.currency_id || null,
        exchange_rate: rateFor(fromCurr?.code, from.exchange_rate),
        lines: [
          { account_id: payableFor(from.supplier_id), debit_amount: amt, credit_amount: 0, description: desc, party_type: 'supplier', party_id: from.supplier_id },
          { account_id: payableFor(to.supplier_id), debit_amount: 0, credit_amount: amt, description: desc, party_type: 'supplier', party_id: to.supplier_id },
        ],
        created_by: username,
        branch_id: branchId,
      });
    }

    // Costs can arrive in stages — a row only posts its journal once (tracked via
    // `journaled`), and an Internal/Tour Operator row waits until the invoice is
    // finalized (i.e. the tour actually happened) before it ever posts at all.
    const finalItems = [];
    for (const s of costItems) {
      const isInternal = s.cost_type === 'internal';
      if (s.journaled || (isInternal && !isFinalized)) { finalItems.push(s); continue; }

      const suppCurr = currencies.find(c => c.id === s.currency_id);
      const totalCost = s.per_pax ? s.cost_amount * postQty(s) : s.cost_amount;
      if (!totalCost) { finalItems.push({ ...s, journaled: true, journal_entry_id: null }); continue; }
      const lineDesc = [invoice.invoice_number, invoice.clients?.full_name, s.notes || s.supplier_label].filter(Boolean).join(' — ');
      const postedJe = await postJournal({
        date: invoice.issued_date || today(),
        description: lineDesc,
        reference_type: refType,
        reference_id: invoiceId,
        currency_id: s.currency_id || null,
        exchange_rate: rateFor(suppCurr?.code, s.exchange_rate),
        lines: isInternal ? [
          { account_id: expense, debit_amount: totalCost, credit_amount: 0, description: lineDesc },
          { account_id: ACCOUNTS.OTHER_PAYABLES, debit_amount: 0, credit_amount: totalCost, description: lineDesc },
        ] : [
          { account_id: expense, debit_amount: totalCost, credit_amount: 0, description: lineDesc },
          { account_id: payableFor(s.supplier_id), debit_amount: 0, credit_amount: totalCost, description: lineDesc, party_type: 'supplier', party_id: s.supplier_id },
        ],
        created_by: username,
        branch_id: branchId,
      });
      finalItems.push({ ...s, journaled: true, journal_entry_id: postedJe?.id || null });
    }

    const invPatch = { cost_items: finalItems };
    if (lineTypesDirty) invPatch.line_items = resolvedLineItems;
    await supabase.from('invoices').update(invPatch).eq('id', invoiceId);
    if (lineTypesDirty) { invoice.line_items = resolvedLineItems; setLineTypesDirty(false); }
    invoice.cost_items = finalItems;
    // Reload rows from the saved, canonical cost_items (drops any stray blank rows and
    // reflects each row's up-to-date `journaled` state).
    setSuppliers(finalItems.map(c => ({
      supplier_id: c.supplier_id || '',
      supplier_label: c.supplier_label || '',
      sub_supplier_id: c.sub_supplier_id || null, sub_supplier_label: c.sub_supplier_label || null,
      cost_amount: String(c.cost_amount ?? ''),
      currency_id: c.currency_id || '',
      exchange_rate: String(c.exchange_rate ?? '1'),
      notes: c.notes || '',
      per_pax: !!c.per_pax,
      cost_type: c.cost_type || 'supplier',
      journaled: !!c.journaled,
      line_item_index: c.line_item_index != null ? String(c.line_item_index) : '',
      pax_type_filter: c.pax_type_filter || '',
    })));

    // Commission lines — never touch one that's already been approved/paid. Otherwise
    // upsert each so re-saving costing keeps them in sync, and delete any line whose
    // agent/amount was removed or "Include" was unticked. One agent can stack multiple
    // rules (e.g. a flat per-visa amount plus a separate GP-tiered cut) as separate rows.
    const updatedLines = await Promise.all(commissionLines.map(async (line) => {
      if (line.locked) return line;
      const wants = line.agent_id && line.include && parseFloat(line.commission_amount) > 0;
      if (wants) {
        const linePType = lineItemType(line);
        const payload = {
          agent_ref_id: line.agent_id,
          reference_type: COMMISSION_REF_TYPE[linePType] || 'TICKET',
          reference_id: invoiceId,
          product_type: PRODUCT_TYPE_MAP[linePType] || 'Ticket',
          gp_amount: gpLKR,
          commission_percent: parseFloat(line.commission_percent) || null,
          commission_amount: parseFloat(line.commission_amount),
          currency_id: lkrId,
          commission_status: 'PENDING',
          commission_table_id: line.commission_table_id || null,
          units: parseFloat(line.units) || null,
        };
        if (line.id) {
          const { error: updErr } = await supabase.from('commissions').update(payload).eq('id', line.id);
          if (updErr) throw updErr;
          return line;
        }
        const { data: newComm, error: insErr } = await supabase.from('commissions').insert(payload).select().single();
        if (insErr) throw insErr;
        return newComm ? { ...line, id: newComm.id, key: newComm.id } : line;
      }
      if (line.id) await supabase.from('commissions').delete().eq('id', line.id);
      return { ...line, id: null };
    }));
    setCommissionLines(updatedLines);
  }

  async function saveCosting() {
    setSaving(true); setError('');
    try {
      await persistCosting();
      navigate(`/invoices/${invoiceId}`);
    } catch (err) { setError(err.message); setSaving(false); }
  }

  // Marks costing as complete for this invoice — requires admin passcode since it's the
  // point of no return that removes the invoice from Costing Pending. Also the moment any
  // deferred Internal/Tour Operator cost rows finally post their journal (passing
  // willFinalize=true so persistCosting() treats the invoice as finalized for this pass,
  // before the DB flag itself is set).
  async function finalizeCosting() {
    const hasCostRows = suppliers.some(s => (s.cost_type === 'internal' ? s.supplier_label : s.supplier_id) && s.cost_amount);
    // Service-charge-only invoices genuinely have no cost: the invoice's own DR AR / CR
    // Revenue journal is the whole entry, so there is nothing to post here — just mark it
    // finalized. Only allowed when no cost was ever saved; if rows were saved before and
    // have since been cleared, persistCosting must still run so nothing is left half-posted.
    if (!hasCostRows) {
      if ((invoice.cost_items || []).length) {
        setError('Add at least one supplier or internal cost with an amount before finalizing.'); return;
      }
      if (!window.confirm(`Finalize ${invoice.invoice_number} with NO supplier cost?\n\nUse this only for service-charge-only invoices — the full invoice amount stays as profit.`)) return;
      try { await promptPasscode(); } catch { return; }
      setFinalizing(true); setError('');
      const { error } = await supabase.from('invoices').update({ costing_finalized: true }).eq('id', invoiceId);
      setFinalizing(false);
      if (error) { setError(error.message || 'Failed to finalize costing'); return; }
      navigate('/invoices/costing-pending');
      return;
    }
    try { await promptPasscode(); } catch { return; }
    setFinalizing(true); setError('');
    try {
      await persistCosting(true);
      await supabase.from('invoices').update({ costing_finalized: true }).eq('id', invoiceId);
      navigate('/invoices/costing-pending');
    } catch (err) {
      setError(err.message || 'Failed to finalize costing');
    } finally {
      setFinalizing(false);
    }
  }

  if (!invoice) return <div className="spinner" style={{ padding: 40 }} />;

  const lineItems = resolvedLineItems;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div className="page-header">
        <div>
          <div className="page-title">Add Supplier Costing</div>
          <div className="page-subtitle">
            {invoice.invoice_number} — {invoice.clients?.full_name}
            {invoice.costing_finalized
              ? <span className="badge badge-green" style={{ marginLeft: 10 }}>Finalized</span>
              : <span className="badge badge-yellow" style={{ marginLeft: 10 }}>Not finalized — still in Costing Pending</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(`/invoices/${invoiceId}`)}>Cancel</button>
          <button className="btn btn-secondary" onClick={saveCosting} disabled={saving || finalizing}
            style={{ background: 'rgba(240, 112, 64, 0.1)', borderColor: '#f07040', color: '#f07040' }}>
            {saving ? 'Saving…' : 'Save Costing'}
          </button>
          {!invoice.costing_finalized && (
            <button className="btn btn-secondary" onClick={finalizeCosting} disabled={saving || finalizing}
              style={{ background: 'rgba(22, 163, 74, 0.1)', borderColor: '#16a34a', color: '#16a34a' }}
              title="Confirms no more supplier costs are coming for this invoice — requires admin passcode">
              {finalizing ? 'Finalizing…' : 'Final Cost'}
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}
      {PasscodeModal}

      {/* Invoice summary — read only */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Invoice Summary</div>
        <div style={{ display: 'flex', gap: 32, marginBottom: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Client</div>
            <div style={{ fontWeight: 700 }}>{invoice.clients?.full_name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.clients?.client_code}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Invoice</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)' }}>{invoice.invoice_number}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{invoice.issued_date}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Type</div>
            <div style={{ textTransform: 'capitalize', fontWeight: 600 }}>{invoice.source_type || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total Sell</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>LKR {fmt(totalSell)}</div>
          </div>
        </div>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Description</th>
              {PAX_TYPES.length > 0 && <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Pax Type</th>}
              <th style={{ padding: '6px 10px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600 }}>Qty</th>
              <th style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--text-muted)', fontWeight: 600 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((l, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 10px' }}>{l.description || l.type}</td>
                {PAX_TYPES.length > 0 && (
                  <td style={{ padding: '4px 10px', width: 190 }}>
                    <select className="form-input" style={{ fontSize: 12, padding: '4px 6px' }} value={lineType(l)}
                      onChange={e => setLineType(i, e.target.value)}>
                      {!lineType(l) && <option value="">— Pax type —</option>}
                      {PAX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                )}
                <td style={{ padding: '6px 10px', textAlign: 'center' }}>{l.qty || 1}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Supplier cost entry */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Supplier Costs</div>
          <button className="btn btn-secondary btn-sm" onClick={addSupplier}>+ Add Supplier</button>
        </div>
        {suppliers.map((s, i) => (
          <div key={i} style={{ marginBottom: 12, opacity: s.journaled ? 0.7 : 1 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: s.journaled ? 'default' : 'pointer' }}>
                <input type="radio" checked={(s.cost_type || 'supplier') === 'supplier'} disabled={s.journaled}
                  onChange={() => updateSupplier(i, { cost_type: 'supplier', supplier_label: '' })} /> Supplier
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: s.journaled ? 'default' : 'pointer' }}>
                <input type="radio" checked={s.cost_type === 'internal'} disabled={s.journaled}
                  onChange={() => updateSupplier(i, { cost_type: 'internal', supplier_id: '', supplier_label: '' })} /> Internal / Tour Operator Cost
              </label>
              {s.journaled && <span className="badge badge-green" style={{ fontSize: 10 }}>Posted</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: resolvedLineItems.length > 1 ? '1.6fr 0.8fr 0.7fr 0.7fr 1.3fr auto auto' : '2fr 1fr 1fr 1fr auto auto', gap: 10, alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label">{s.cost_type === 'internal' ? 'Description' : 'Supplier'}</label>}
                {s.cost_type === 'internal' ? (
                  <input className="form-input" placeholder="e.g. Guide allowance — Kasun" value={s.supplier_label}
                    disabled={s.journaled}
                    onChange={e => updateSupplier(i, { supplier_label: e.target.value })} />
                ) : (
                  <SupplierPicker row={s} disabled={s.journaled} onChange={patch => updateSupplier(i, patch)} />
                )}
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label">Cost Amount</label>}
                <input className="form-input" type="number" placeholder="0.00" value={s.cost_amount} disabled={s.journaled}
                  onChange={e => updateSupplier(i, { cost_amount: e.target.value })} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label">Currency</label>}
                <select className="form-input" value={s.currency_id || lkrId} disabled={s.journaled} onChange={e => updateSupplier(i, { currency_id: e.target.value })}>
                  {currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label">Ex. Rate</label>}
                <input className="form-input" type="number" placeholder="1" value={s.exchange_rate} disabled={s.journaled}
                  onChange={e => updateSupplier(i, { exchange_rate: e.target.value })} />
              </div>
              {resolvedLineItems.length > 1 && (
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">Applies To</label>}
                  {/* Never disabled by s.journaled, unlike every other field on this row — this
                      is a pure display/reporting attribute (excluded from persistCosting's sig()),
                      so it's always safe to change even after the cost's journal has posted. */}
                  <select className="form-input" value={s.pax_type_filter ? `type:${s.pax_type_filter}` : (s.line_item_index ?? '')}
                    onChange={e => {
                      const v = e.target.value;
                      if (v.startsWith('type:')) updateSupplier(i, { pax_type_filter: v.slice(5), line_item_index: '' });
                      else updateSupplier(i, { line_item_index: v, pax_type_filter: '' });
                    }}>
                    <option value="">Shared (Split Evenly)</option>
                    {PAX_TYPES.map(t => <option key={t} value={`type:${t}`}>Every "{t}" line</option>)}
                    {resolvedLineItems.map((item, idx) => (
                      <option key={idx} value={idx}>{paxLabel(item.description, idx)}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {i === 0 && <label className="form-label" style={{ whiteSpace: 'nowrap' }}>Per Pax</label>}
                <input type="checkbox" checked={!!s.per_pax} disabled={s.journaled}
                  onChange={e => updateSupplier(i, { per_pax: e.target.checked })}
                  style={{ width: 18, height: 18, cursor: 'pointer', marginTop: i === 0 ? 0 : 10 }} />
              </div>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: i === 0 ? 18 : 0 }} disabled={s.journaled} onClick={() => removeSupplier(i)}>x</button>
            </div>
            <input className="form-input" placeholder="Notes (e.g. Hotel 3 nights, Adult x2…)" value={s.notes || ''} disabled={s.journaled}
              style={{ marginTop: 6, fontSize: 12 }}
              onChange={e => updateSupplier(i, { notes: e.target.value })} />
            {s.cost_type === 'internal' && !s.journaled && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Posts to Other Payables (2060), not a supplier — and won't hit the books until this invoice's costing is finalized.
              </div>
            )}
          </div>
        ))}

        {perPassengerMargins.length > 1 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>GP Per Passenger</div>
            <table style={{ width: '100%', fontSize: 12 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                  <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 4 }}>Passenger</th>
                  <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Sell</th>
                  <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Margin</th>
                  <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Commission</th>
                  <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>GP</th>
                </tr>
              </thead>
              <tbody>
                {perPassengerMargins.map((p, i) => {
                  const comm = perPaxCommission[i] || 0;
                  const netGP = p.margin - comm;
                  return (
                    <tr key={i}>
                      <td style={{ padding: '2px 8px 2px 0', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11.5 }} title={p.description}>{p.description || `Passenger ${i + 1}`}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(p.sell)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(p.margin)}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{comm > 0 ? `(${fmt(comm)})` : fmt(0)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: netGP >= 0 ? 'var(--success, #22c55e)' : 'var(--danger)' }}>{fmt(netGP)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8, display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Sell (Cumulative)</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>LKR {fmt(totalSell)}</div>
          </div>
          <div style={{ color: 'var(--text-muted)' }}>−</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Cost (Cumulative)</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>LKR {fmt(totalCostLKR)}</div>
          </div>
          {totalCommission > 0 && (
            <>
              <div style={{ color: 'var(--text-muted)' }}>−</div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Commission</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>LKR {fmt(totalCommission)}</div>
              </div>
            </>
          )}
          <div style={{ color: 'var(--text-muted)' }}>=</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {totalCommission > 0 ? 'Net GP (Cumulative)' : 'Gross Profit (Cumulative)'}
            </div>
            {(() => {
              const netGP = gpLKR - totalCommission;
              return (
                <div style={{ fontSize: 15, fontWeight: 700, color: netGP >= 0 ? 'var(--success, #22c55e)' : 'var(--danger)' }}>
                  LKR {fmt(netGP)}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Commission — one agent can stack more than one rule (e.g. a flat per-visa
          amount plus a separate GP-tiered cut), so this is a repeatable list. */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Commission</div>
          <button className="btn btn-secondary btn-sm" onClick={addCommissionLine}>+ Add Commission</button>
        </div>
        {commissionLines.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No commission lines yet.</div>
        )}
        {commissionLines.map((line, i) => {
          const pendingUnlock = !!line.id && !line.locked && !unlockedCommissionKeys.includes(line.key);
          const readOnly = line.locked || pendingUnlock;
          return (
          <div key={line.key} style={{ marginBottom: 12, paddingBottom: i < commissionLines.length - 1 ? 12 : 0, borderBottom: i < commissionLines.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ display: 'grid', gridTemplateColumns: commissionItemType === 'package' ? '1.6fr 1fr 1.2fr 0.7fr 1fr auto auto' : '2fr 1.2fr 0.7fr 1fr auto auto', gap: 12, alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label">Agent</label>}
                {readOnly ? (
                  <div className="form-input" style={{ background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-muted)' }}>{line.agent_label || '—'}</div>
                ) : (
                  <TypeSearch
                    fetchFn={async (q) => {
                      const { data } = await supabase.from('agents').select('id, tls_code, full_name').or(`full_name.ilike.%${q}%,tls_code.ilike.%${q}%`).limit(20);
                      return (data || []).map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` }));
                    }}
                    value={line.agent_label}
                    displayKey="label"
                    placeholder="Search agent…"
                    onSelect={a => updateCommissionLine(line.key, { agent_id: a.id, agent_label: `${a.tls_code} — ${a.full_name}` })}
                    onClear={() => updateCommissionLine(line.key, { agent_id: '', agent_label: '', commission_amount: '', commission_percent: '', commission_table_id: '' })}
                  />
                )}
              </div>
              {commissionItemType === 'package' && (
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label" title="This invoice bundles more than one cost type — pick which one this commission line is for">Type</label>}
                  {readOnly ? (
                    <div className="form-input" style={{ background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-muted)' }}>{PRODUCT_TYPE_MAP[lineItemType(line)]}</div>
                  ) : (
                    <select className="form-input" value={line.product_type || 'package'}
                      onChange={e => updateCommissionLine(line.key, { product_type: e.target.value, rule_name: '', commission_amount: '', commission_percent: '', commission_table_id: '' })}>
                      <option value="package">Package</option>
                      <option value="visa">Visa</option>
                      <option value="ticket">Ticket</option>
                    </select>
                  )}
                </div>
              )}
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label">Rule</label>}
                {(availableRuleNames[lineItemType(line)] || []).length > 0 && !readOnly ? (
                  <select className="form-input" value={line.rule_name} onChange={e => updateCommissionLine(line.key, { rule_name: e.target.value })}>
                    <option value="">— Select rule —</option>
                    {availableRuleNames[lineItemType(line)].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                ) : (
                  <div className="form-input" style={{ background: 'var(--bg-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
                    {line.commission_table_id
                      ? (line.rule_name || (line.commission_percent ? `${line.commission_percent}% of GP` : 'Fixed amount'))
                      : line.agent_id ? 'No matching rule' : '—'}
                  </div>
                )}
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label" title="How many visas/tickets/units this agent covers — matters for per-unit rules, and lets you split one invoice across several agents">Units</label>}
                <input className="form-input" type="number" min="0" value={line.units} disabled={readOnly}
                  onChange={e => updateCommissionLine(line.key, { units: e.target.value })} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                {i === 0 && <label className="form-label">Commission (LKR)</label>}
                <input className="form-input" type="number" value={line.commission_amount}
                  onChange={e => !line.commission_table_id && updateCommissionLine(line.key, { commission_amount: e.target.value })}
                  placeholder="0.00"
                  readOnly={!!line.commission_table_id || readOnly}
                  style={(line.commission_table_id || readOnly) ? { background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-muted)', cursor: 'default' } : {}} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: i === 0 ? 6 : 0, cursor: readOnly ? 'default' : 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={line.include} disabled={readOnly} onChange={e => updateCommissionLine(line.key, { include: e.target.checked })} />
                Include
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                {pendingUnlock && (
                  <button className="btn btn-secondary btn-sm" onClick={() => unlockCommissionLine(line.key)}>Edit</button>
                )}
                <button className="btn btn-secondary btn-sm" disabled={line.locked} onClick={() => removeCommissionLine(line)}>x</button>
              </div>
            </div>
            {line.locked && <div style={{ fontSize: 11, color: 'var(--success, #16a34a)', marginTop: 4 }}>Already approved/paid — locked</div>}
            {pendingUnlock && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Saved commission — editing or removing requires the admin passcode</div>}
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main InvoiceNew ────────────────────────────────────────────────────────────

export default function InvoiceNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, branchId } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const PAX_TYPES = usePaxTypes();

  const [tab, setTab] = useState('direct');
  const [currencies, setCurrencies] = useState([]);
  const [lkrId, setLkrId] = useState('');

  const [itemType, setItemType] = useState('ticket');
  const [clientId, setClientId] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [lineItems, setLineItems] = useState([{ type: 'Adult', rate: '', qty: '1' }]);
  const [currencyId, setCurrencyId] = useState('');
  const [issuedDate, setIssuedDate] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [suppliers, setSuppliers] = useState([emptySupplier()]);
  const [showCost, setShowCost] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ticket fields — a repeatable list of flight segments (airline/PNR/route per leg),
  // since connecting flights need more than one row.
  const [segments, setSegments] = useState([emptyTicketSegment()]);
  const addSegment = () => setSegments(s => [...s, emptyTicketSegment()]);
  const removeSegment = (key) => setSegments(s => s.filter(seg => seg.key !== key));
  const updateSegment = (key, field, val) => setSegments(s => s.map(seg => seg.key === key ? { ...seg, [field]: val } : seg));
  const [travelDate, setTravelDate] = useState('');
  // visa fields
  const [country, setCountry] = useState('');
  const [visaType, setVisaType] = useState('');
  // package fields
  const [bookingId, setBookingId] = useState('');
  const [bookingLabel, setBookingLabel] = useState('');
  // Group vs FIT choice for a package invoice — Group must link a real package (its
  // costing then pulls in below, see pullPackageCosts); FIT keeps working exactly as
  // before (manual Supplier Costs, lands in Costing Pending unless filled in).
  const [packageMode, setPackageMode] = useState('');
  const [packageId, setPackageId] = useState('');
  const [packageLabel, setPackageLabel] = useState('');

  // quotation tab
  const [quotId, setQuotId] = useState('');
  const [quotLabel, setQuotLabel] = useState('');

  // commission — a repeatable list, since one agent can earn more than one rule at once
  // (e.g. a flat per-visa amount plus a separate GP-tiered cut)
  const [commissionLines, setCommissionLines] = useState([]);
  const [availableRuleNames, setAvailableRuleNames] = useState([]);
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [defaultAgentId, setDefaultAgentId] = useState('');
  const [defaultAgentLabel, setDefaultAgentLabel] = useState('');

  // derived
  const totalAmount = lineItems.reduce((sum, l) => sum + (parseFloat(l.rate) || 0) * (parseInt(l.qty) || 0), 0);
  const totalPax = lineItems.reduce((sum, l) => sum + (parseInt(l.qty) || 0), 0) || 1;
  // A cost row assigned to one specific passenger (line_item_index set) scales by that
  // passenger's own qty when per_pax, or is a flat total when not — see the matching
  // comment on the edit-invoice path above.
  const assignedQty = (c) => {
    const item = lineItems[parseInt(c.line_item_index)];
    return item ? (parseInt(item.qty) || 1) : 1;
  };
  const isAssigned = (c) => c.line_item_index !== '' && c.line_item_index != null;
  // A cost row pulled from a package's per-pax-type rate (Adult/Child With Bed/Child
  // Without Bed/Infant) applies across every line item of that type, not just one — unlike
  // a manually-assigned single-passenger cost (line_item_index), there can be several pax
  // of the same type spread across different line items.
  const isTypeFiltered = (c) => !!c.pax_type_filter;
  const typeMatchQty = (c) => lineItems.reduce((s, it) => it.type === c.pax_type_filter ? s + (parseInt(it.qty) || 1) : s, 0);
  // Pax multiplier for POSTING a per_pax row — see the matching comment in CostingEntry.
  const postQty = (c) => isTypeFiltered(c) ? typeMatchQty(c) : isAssigned(c) ? assignedQty(c) : totalPax;
  const totalCostLKR = suppliers.reduce((sum, s) => {
    const base = (parseFloat(s.cost_amount) || 0) * (parseFloat(s.exchange_rate) || 1);
    if (isTypeFiltered(s)) return sum + (s.per_pax ? base * typeMatchQty(s) : base);
    if (isAssigned(s)) return sum + (s.per_pax ? base * assignedQty(s) : base);
    return sum + (s.per_pax ? base * totalPax : base);
  }, 0);
  const gpLKR = totalAmount - totalCostLKR;
  const gpPct = totalAmount > 0 ? (gpLKR / totalAmount * 100) : 0;

  // Per-passenger margin — see the matching comment on the edit-invoice path above for why:
  // each revenue line's own sell amount minus its own cost share (a row assigned to one
  // passenger attributes 100% to them and 0 to everyone else; a "Shared" row still splits
  // evenly), so GP-tiered commissions tier off each passenger's margin rather than the
  // invoice's blended total.
  const perPassengerMargins = lineItems.map((l, lIdx) => {
    const qty = parseInt(l.qty) || 1;
    const sell = (parseFloat(l.rate) || 0) * qty;
    const costShare = suppliers.reduce((s, c) => {
      const base = (parseFloat(c.cost_amount) || 0) * (parseFloat(c.exchange_rate) || 1);
      if (isTypeFiltered(c)) {
        if (l.type !== c.pax_type_filter) return s;
        const matchQty = typeMatchQty(c);
        return s + (c.per_pax ? base * qty : (matchQty > 0 ? (base * qty) / matchQty : 0));
      }
      if (isAssigned(c)) {
        if (parseInt(c.line_item_index) !== lIdx) return s;
        return s + (c.per_pax ? base * qty : base);
      }
      return s + (c.per_pax ? base * qty : (totalPax > 0 ? (base * qty) / totalPax : 0));
    }, 0);
    // description/sell/costShare are carried for the per-pax GP display only — the
    // commission lookup above only ever reads .margin.
    return { description: l.type, qty, sell, costShare, margin: sell - costShare };
  });
  const commissionProductType = PRODUCT_TYPE_MAP[itemType] || 'Ticket';
  const totalCommission = commissionLines.reduce((s, l) => s + (l.include ? (parseFloat(l.commission_amount) || 0) : 0), 0);
  // Each passenger's own share of commission — see the matching comment on the edit-invoice
  // path above.
  const perPaxCommission = perPassengerMargins.map((_, i) =>
    commissionLines.reduce((s, l) => s + (l.include && l._perPax && l._perPax[i] != null ? l._perPax[i] : 0), 0)
  );

  const addCommissionLine = () => setCommissionLines(ls => [...ls, emptyCommissionLine(defaultAgentId, defaultAgentLabel, totalPax)]);
  const removeCommissionLine = (key) => setCommissionLines(ls => ls.filter(l => l.key !== key));
  const updateCommissionLine = (key, fields) => setCommissionLines(ls => ls.map(l => l.key === key ? { ...l, ...fields } : l));

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchConfirmed, setBranchConfirmed] = useState(false);
  useEffect(() => { if (branchId && !selectedBranchId) setSelectedBranchId(branchId); }, [branchId]);

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
    supabase.from('currencies').select('id, code').order('code').then(({ data }) => {
      setCurrencies(data || []);
      const lkr = (data || []).find(c => c.code === 'LKR');
      if (lkr) { setLkrId(lkr.id); setCurrencyId(lkr.id); }
    });

    const type = searchParams.get('type');
    const clientParam = searchParams.get('client');
    const clientNameParam = searchParams.get('clientName');
    const invoiceTypeParam = searchParams.get('invoiceType');
    if (type === 'booking') setItemType('package'); else if (type) setItemType(type);
    if (clientParam) setClientId(clientParam);
    if (clientNameParam) setClientLabel(decodeURIComponent(clientNameParam));

    if (type === 'visa') {
      const sourceId = searchParams.get('source');
      if (sourceId) {
        supabase.from('visas')
          .select('*, visa_types(name, country), sell_currency:sell_currency_id(code)')
          .eq('id', sourceId).single()
          .then(({ data: v }) => {
            if (!v) return;
            if (v.visa_types?.country) setCountry(v.visa_types.country);
            if (v.visa_types?.name) setVisaType(v.visa_types.name);
            if (v.sell_price) setLineItems([{ type: 'Adult', rate: String(v.sell_price), qty: '1' }]);
          });
      }
    }

    if (type === 'ticket') {
      const sourceId = searchParams.get('source');
      if (sourceId) {
        supabase.from('tickets')
          .select('*, sell_currency:sell_currency_id(code)')
          .eq('id', sourceId).single()
          .then(({ data: t }) => {
            if (!t) return;
            if (t.segments?.length) {
              setSegments(t.segments.map(s => ({ airline: '', pnr: '', route_from: '', route_to: '', ...s, key: `seg-${++ticketSegmentSeq}` })));
            } else if (t.airline) {
              setSegments([{ ...emptyTicketSegment(), airline: t.airline, pnr: t.pnr || '', route_from: t.route_from || '', route_to: t.route_to || '' }]);
            }
            if (t.travel_date) setTravelDate(t.travel_date.slice(0, 10));
            if (t.sell_price) setLineItems([{ type: 'Adult', rate: String(t.sell_price), qty: '1' }]);
          });
      }
    }

    if (type === 'booking') {
      const sourceId = searchParams.get('source');
      if (sourceId) {
        supabase.from('bookings')
          .select('*, packages(package_name, package_code), sell_currency:sell_currency_id(code)')
          .eq('id', sourceId).single()
          .then(({ data: b }) => {
            if (!b) return;
            setBookingId(b.id);
            setBookingLabel(b.packages?.package_name || b.booking_ref || '');
            const sellTotal = parseFloat(b.total_sell || b.sell_price || 0);
            const pax = b.num_pax || 1;
            if (sellTotal > 0) setLineItems([{ type: 'Adult', rate: String(sellTotal / pax), qty: String(pax) }]);
            if (b.sell_currency_id) setCurrencyId(b.sell_currency_id);
            if (b.travel_date) setTravelDate(b.travel_date.slice(0, 10));
          });
      }
    }

    // fetch agent if client param given
    if (clientParam) fetchAgentForClient(clientParam);
  }, []);

  // Every rule name active for this product type — lets staff add one line per rule
  // (e.g. a flat per-visa rule AND a separate GP-tiered rule for the same agent).
  useEffect(() => {
    setRulesLoaded(false);
    supabase.from('commission_tables')
      .select('rule_name')
      .eq('product_type', commissionProductType)
      .eq('is_active', true)
      .then(({ data }) => {
        setAvailableRuleNames([...new Set((data || []).map(r => r.rule_name).filter(Boolean))]);
        setRulesLoaded(true);
      });
  }, [commissionProductType]);

  // Auto-lookup each line's commission whenever its agent/rule or the GP changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const updated = await Promise.all(commissionLines.map(async (line) => {
        if (!line.agent_id) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };

        // Always fetch every active rule for this product type ourselves — deciding
        // ambiguity from this same query's result, not from the separately-fetched
        // availableRuleNames state, which can lag behind and cause this lookup to
        // silently guess a rule (e.g. picking another agent's per-visa rate) before
        // that other fetch has resolved.
        const { data: allRows } = await supabase.from('commission_tables').select('*').eq('product_type', commissionProductType).eq('is_active', true).limit(200);
        if (!allRows?.length) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
        const distinctRuleNames = [...new Set(allRows.map(r => r.rule_name).filter(Boolean))];
        if (!line.rule_name && distinctRuleNames.length > 1) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
        const rows = line.rule_name ? allRows.filter(r => r.rule_name === line.rule_name) : allRows;
        if (!rows.length) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };

        const fixedRule = rows.find(r => r.commission_type === 'FIXED');
        if (fixedRule) return { ...line, commission_table_id: fixedRule.id, commission_amount: String(parseFloat(fixedRule.fixed_amount) || 0), commission_percent: '' };

        // COUNT_BASED — a per-unit rate (e.g. per visa), optionally tiered by unit-count
        // range (gp_from/gp_to reused as unit bounds, same pattern as GP tiers). Multiplies
        // by this line's own `units`, not the invoice's GP.
        const countRows = rows.filter(r => r.commission_type === 'COUNT_BASED');
        if (countRows.length) {
          const units = parseFloat(line.units) || 0;
          const tier = countRows.find(r => parseFloat(r.gp_from) <= units && (r.gp_to === null || parseFloat(r.gp_to) >= units)) || countRows[0];
          if (!tier || units <= 0) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
          return { ...line, commission_table_id: tier.id, commission_amount: String((units * (parseFloat(tier.amount_per_unit) || 0)).toFixed(2)), commission_percent: '' };
        }

        // GP-tiered: apply the rule to each passenger's own margin and sum — see the matching
        // block on the edit-invoice path above for the full reasoning.
        const marginsToTier = perPassengerMargins.length ? perPassengerMargins : [{ qty: totalPax, margin: gpLKR }];
        // Per-passenger breakdown of this line's own commission, aligned index-for-index with
        // perPassengerMargins — read by the per-passenger display table below to show each
        // passenger's own commission and net GP.
        const perPax = perPassengerMargins.length ? perPassengerMargins.map(() => 0) : null;
        let total = 0, anyTierMatched = false;
        marginsToTier.forEach(({ margin }, idx) => {
          if (margin <= 0) return;
          const tier = rows.find(r => parseFloat(r.gp_from) <= margin && (r.gp_to === null || parseFloat(r.gp_to) >= margin));
          if (!tier) return;
          anyTierMatched = true;
          let amt = 0;
          if (parseFloat(tier.fixed_amount) > 0) amt = parseFloat(tier.fixed_amount);
          else if (parseFloat(tier.commission_percent) > 0) amt = margin * parseFloat(tier.commission_percent) / 100;
          total += amt;
          if (perPax) perPax[idx] = amt;
        });
        if (!anyTierMatched) return { ...line, commission_amount: '', commission_percent: '', commission_table_id: '' };
        return { ...line, commission_amount: total.toFixed(2), commission_percent: '', commission_table_id: '', _perPax: perPax };
      }));
      if (!cancelled) setCommissionLines(updated);
    })();
    return () => { cancelled = true; };
    // Only re-run when the inputs that affect the lookup change, not every time commissionLines
    // itself changes (which would loop, since this effect writes to it).
  }, [commissionLines.map(l => `${l.agent_id}|${l.rule_name}|${l.units}`).join(','), perPassengerMargins.map(m => m.margin.toFixed(2)).join(','), gpLKR, commissionProductType, availableRuleNames.join(','), rulesLoaded]); // eslint-disable-line

  async function fetchAgentForClient(cId) {
    const { data } = await supabase.from('clients')
      .select('care_of_agent_id, agents:care_of_agent_id(id, tls_code, full_name)')
      .eq('id', cId).maybeSingle();
    if (data?.agents) {
      const label = `${data.agents.tls_code} — ${data.agents.full_name}`;
      setDefaultAgentId(data.agents.id);
      setDefaultAgentLabel(label);
      setCommissionLines([emptyCommissionLine(data.agents.id, label, totalPax)]);
    } else {
      setDefaultAgentId(''); setDefaultAgentLabel('');
      setCommissionLines([]);
    }
  }

  async function selectClient(c) {
    setClientId(c.id);
    setClientLabel(c.client_code || c.full_name);
    await fetchAgentForClient(c.id);
  }

  function clearItemFields() {
    setSegments([emptyTicketSegment()]); setTravelDate('');
    setCountry(''); setVisaType('');
    setBookingId(''); setBookingLabel('');
    setPackageMode(''); setPackageId(''); setPackageLabel('');
  }

  // Pulls a Group package's real supplier costs (per-pax, exchange_rate:1 — already
  // FX-converted at the rate card level) into the suppliers state, so staff don't retype
  // them from scratch. A supplier can carry a separate rate per pax_type (Adult/Child With
  // Bed/Child Without Bed/Infant) — each becomes its own cost row tagged with
  // pax_type_filter, so it only ever applies to line items of that exact type (never
  // blended/shared across everyone the way an untagged row would be), and
  // totalCostLKR/perPassengerMargins scale each by however many line items of that type
  // end up on the invoice. Shared by the direct-entry Package picker and by
  // fillFromQuotation() for a pro forma that already linked one.
  async function pullPackageCosts(pkgId) {
    const { data: rates } = await supabase
      .from('package_supplier_rates')
      .select('cost_per_pax, budgeted_fx_rate, currency_id, is_included, pax_type, package_suppliers!inner(supplier_id, suppliers(name))')
      .eq('package_suppliers.package_id', pkgId);
    const byKey = {};
    for (const r of rates || []) {
      if (r.is_included) continue;
      const sid = r.package_suppliers?.supplier_id;
      if (!sid) continue;
      const paxType = r.pax_type || 'Adult';
      const key = `${sid}::${paxType}`;
      if (!byKey[key]) byKey[key] = { sid, paxType, costPerPax: 0, fxRate: parseFloat(r.budgeted_fx_rate || 1), currency_id: r.currency_id || null, label: r.package_suppliers?.suppliers?.name || '' };
      byKey[key].costPerPax += parseFloat(r.cost_per_pax || 0);
    }
    // "Common (All Pax)" is a genuinely shared cost (e.g. a tour operator/guide fee) — it
    // isn't tied to one pax type, so it stays untagged (shared/split across every line item
    // on the invoice), exactly like every cost worked before pax types existed at all.
    const pulled = Object.values(byKey).map(info => {
      const isCommon = info.paxType === 'Common (All Pax)';
      return {
        supplier_id: info.sid, supplier_label: isCommon ? info.label : `${info.label} (${info.paxType})`,
        cost_amount: String(info.costPerPax * info.fxRate),
        currency_id: info.currency_id || lkrId, exchange_rate: '1', notes: '',
        per_pax: true, cost_type: 'supplier', journaled: false,
        line_item_index: '', pax_type_filter: isCommon ? '' : info.paxType,
      };
    });
    if (pulled.length) setSuppliers(pulled);
  }

  function resetAll() {
    setItemType('ticket');
    setClientId(''); setClientLabel('');
    setDefaultAgentId(''); setDefaultAgentLabel('');
    setLineItems([{ type: 'Adult', rate: '', qty: '1' }]);
    setIssuedDate(today()); setDueDate(''); setNotes('');
    setSuppliers([emptySupplier()]);
    setQuotId(''); setQuotLabel('');
    setCommissionLines([]);
    clearItemFields();
    setError('');
    const lkr = currencies.find(c => c.code === 'LKR');
    if (lkr) setCurrencyId(lkr.id);
  }

  function switchTab(t) { setTab(t); resetAll(); }

  async function fillFromQuotation(q) {
    setQuotId(q.id);
    setQuotLabel(`${q.quotation_number} — ${q.clients?.full_name || ''}`);
    setItemType(q.item_type || 'ticket');
    setClientId(q.client_id || '');
    setClientLabel(q.clients?.full_name || '');
    const savedLines = q.op_data?.line_items;
    if (savedLines?.length) {
      setLineItems(savedLines.map(l => ({ type: l.type || 'Adult', name: l.name || '', rate: String(l.unitPrice || ''), qty: String(l.qty || '1') })));
    } else {
      setLineItems([{ type: 'Adult', rate: q.amount_per_pax ? String(q.amount_per_pax) : '', qty: q.num_pax ? String(q.num_pax) : '1' }]);
    }
    if (q.currency_id) setCurrencyId(q.currency_id);
    if (q.notes) setNotes(q.notes);
    clearItemFields();
    const op = q.op_data || {};
    if (q.item_type === 'ticket') {
      if (op.segments?.length) {
        setSegments(op.segments.map(s => ({ airline: '', pnr: '', route_from: '', route_to: '', ...s, key: `seg-${++ticketSegmentSeq}` })));
      } else {
        const parts = op.route ? op.route.split(/→|->/).map(s => s.trim()) : [];
        setSegments([{ ...emptyTicketSegment(), airline: op.airline || '', pnr: op.pnr || '', route_from: parts[0] || '', route_to: parts[1] || '' }]);
      }
      setTravelDate(op.travel_date || '');
    } else if (q.item_type === 'visa') {
      setCountry(op.country || ''); setVisaType(op.visa_type || '');
    } else if (q.item_type === 'package') {
      setBookingId(op.booking_id || ''); setBookingLabel(op.booking_label || '');
      // Pro forma was linked to a package catalog template — pre-fill the same Group/FIT
      // choice and required-package link this quotation already captured, and pull its
      // real costing in. An older pro forma made before this rule existed won't have
      // op.package_id — packageMode is left unset ('') on purpose, so the operator is
      // forced to choose Group/FIT (and a package, if Group) right here before Create
      // will let them proceed, same as a brand-new direct package invoice.
      if (op.package_id) {
        setPackageId(op.package_id); setPackageLabel(op.package_label || '');
        const { data: pkg } = await supabase.from('packages').select('package_type').eq('id', op.package_id).single();
        setPackageMode(pkg?.package_type === 'FIT' ? 'fit' : 'group');
        await pullPackageCosts(op.package_id);
      }
    }
    if (q.client_id) await fetchAgentForClient(q.client_id);
  }

  const addSupplier = () => setSuppliers(s => [...s, emptySupplier()]);
  const removeSupplier = (i) => setSuppliers(s => s.filter((_, idx) => idx !== i));
  const updateSupplier = (i, fields) => setSuppliers(s => s.map((r, idx) => idx === i ? { ...r, ...fields } : r));

  async function handleCreate() {
    if (!clientId) { setError('Select a client.'); return; }
    if (totalAmount <= 0) { setError('Enter at least one line item with a rate.'); return; }
    if (tab === 'quotation' && !quotId) { setError('Select a quotation.'); return; }
    if (itemType === 'package' && !packageMode) { setError('Choose Group or FIT for this package invoice.'); return; }
    if (itemType === 'package' && packageMode === 'group' && !packageId) { setError('Group package invoices must be linked to a package.'); return; }

    // Block duplicate invoices for the same booking
    if (bookingId) {
      const { data: existing } = await supabase.from('invoices')
        .select('invoice_number, status')
        .eq('source_id', bookingId)
        .neq('status', 'VOID')
        .limit(1);
      if (existing?.length) {
        setError(`Invoice ${existing[0].invoice_number} already exists for this booking. Void it first before creating a new one.`);
        return;
      }
    }

    setSaving(true); setError('');

    try {
      const yy = String(new Date().getFullYear()).slice(-2);
      const { data: maxRow } = await supabase.from('invoices')
        .select('invoice_number').ilike('invoice_number', `TLS-${yy}-%`)
        .order('invoice_number', { ascending: false }).limit(1);
      const lastSeq = maxRow?.[0]?.invoice_number ? parseInt(maxRow[0].invoice_number.split('-').pop()) : 0;
      const num = `TLS-${yy}-${String(lastSeq + 1).padStart(4, '0')}`;

      const costItems = suppliers
        .filter(s => (s.cost_type === 'internal' ? s.supplier_label : s.supplier_id) && s.cost_amount)
        .map(s => ({
          supplier_id: s.cost_type === 'internal' ? null : s.supplier_id,
          supplier_label: s.supplier_label,
          sub_supplier_id: s.sub_supplier_id || null, sub_supplier_label: s.sub_supplier_label || null,
          cost_amount: parseFloat(s.cost_amount),
          currency_id: s.currency_id || lkrId,
          exchange_rate: parseFloat(s.exchange_rate) || 1,
          notes: s.notes,
          per_pax: !!s.per_pax,
          cost_type: s.cost_type || 'supplier',
          line_item_index: s.line_item_index !== '' && s.line_item_index != null ? parseInt(s.line_item_index) : null,
          pax_type_filter: s.pax_type_filter || '',
          // A brand-new invoice is never finalized yet, so Internal/Tour Operator rows
          // defer their journal until Costing Pending finalizes this invoice later.
          journaled: s.cost_type !== 'internal',
        }));

      const builtLineItems = buildLineItemsForSave(itemType, {
        lineItems,
        totalAmount,
        segments, travelDate,
        country, visaType,
        bookingLabel,
      });

      const { data: inv, error: invErr } = await supabase.from('invoices').insert({
        invoice_number: num,
        invoice_type: 'INVOICE',
        client_id: clientId,
        source_type: itemType === 'package' ? 'booking' : itemType,
        source_id: itemType === 'package' ? (bookingId || null) : null,
        // Links the invoice to its package so the package page can list it — a Group
        // package invoice raised here has no booking, so source_id alone can't.
        package_id: itemType === 'package' ? (packageId || null) : null,
        line_items: builtLineItems,
        issued_date: issuedDate,
        due_date: dueDate || null,
        notes: notes || null,
        status: 'SENT',
        quotation_id: quotId || null,
        cost_items: costItems,
        // A Group package's costs are already fully known from its own rate card at the
        // point the package is linked — nothing left for an admin to manually cost, so
        // skip Costing Pending (same precedent as BookingForm.js's Generate Invoice).
        // Every other type (including FIT) keeps landing in Costing Pending by default.
        ...(itemType === 'package' && packageMode === 'group' ? { costing_finalized: true } : {}),
        created_by: username,
        branch_id: selectedBranchId || null,
      }).select().single();
      if (invErr) throw invErr;

      // Revenue journal
      const { revenue, refType } = REVENUE_ACCOUNTS[itemType];
      const currCode = currencies.find(c => c.id === currencyId)?.code;
      if (totalAmount > 0) {
        const je = await postJournal({
          date: issuedDate,
          description: `Invoice ${num}`,
          reference_type: refType,
          reference_id: inv.id,
          currency_id: currencyId || null,
          exchange_rate: rateFor(currCode, 1),
          lines: [
            { account_id: ACCOUNTS.AR, debit_amount: totalAmount, credit_amount: 0, description: `AR - ${num}`, party_type: 'client', party_id: clientId },
            { account_id: revenue, debit_amount: 0, credit_amount: totalAmount, description: `Revenue - ${num}` },
          ],
          created_by: username,
          branch_id: selectedBranchId || null,
        });
        if (je?.id) await supabase.from('invoices').update({ journal_entry_id: je.id }).eq('id', inv.id);
      }

      // Carry through any deposit the client already paid against this exact pro forma —
      // that intent was already given at receipt time, so it applies automatically.
      if (quotId) {
        await applyQuotationDepositToInvoice(quotId, inv.id, clientId, totalAmount, username);
      }

      // Cost journals per supplier — only multiply by pax count when the row is explicitly
      // marked per_pax; otherwise cost_amount is already the correct total (e.g. one row
      // manually entered per passenger, each already at that passenger's own share).
      // Internal/Tour Operator rows are skipped here entirely — a brand-new invoice is never
      // finalized yet, so their journal waits until Costing Pending finalizes it later.
      const { expense, payable: defaultPayable } = COST_ACCOUNTS[itemType];
      const supTypes = await supplierTypesFor(costItems.map(c => c.supplier_id));
      for (const s of costItems.filter(s => s.cost_type !== 'internal')) {
        // AP follows the supplier's own type (see costPayableAccount) — the advance offset
        // below clears the same account.
        const payable = costPayableAccount(supTypes[s.supplier_id], defaultPayable);
        const suppCurr = currencies.find(c => c.id === s.currency_id);
        const totalCost = s.per_pax ? s.cost_amount * postQty(s) : s.cost_amount;
        if (!totalCost) continue;
        const lineDesc = [num, clientLabel, s.notes || (s.sub_supplier_label ? s.supplier_label : null)].filter(Boolean).join(' — ');
        await postJournal({
          date: issuedDate,
          description: lineDesc,
          reference_type: refType,
          reference_id: inv.id,
          currency_id: s.currency_id || null,
          exchange_rate: rateFor(suppCurr?.code, s.exchange_rate),
          lines: [
            { account_id: expense, debit_amount: totalCost, credit_amount: 0, description: lineDesc },
            { account_id: payable, debit_amount: 0, credit_amount: totalCost, description: lineDesc, party_type: 'supplier', party_id: s.supplier_id },
          ],
          created_by: username,
          branch_id: selectedBranchId || null,
        });

        // Offset against any prepaid advance for this supplier on this package — same
        // getSupplierDepositRemaining()/reference_type:'package' pattern BookingForm.js's
        // Generate Invoice flow uses, so a Group package invoice created directly here draws
        // down a real supplier deposit instead of leaving a fresh Payable on top of money
        // already paid. Posted as its own journal (not folded into the cost journal above)
        // so it stays tagged to the package itself, independent of any one invoice — matching
        // how the original deposit payment is tagged, and keeping this out of Admin Edit's
        // invoice-scoped void-and-repost sweep.
        if (itemType === 'package' && packageMode === 'group' && packageId && s.supplier_id) {
          const remaining = await getSupplierDepositRemaining(packageId, s.supplier_id);
          const offset = Math.min(totalCost, Math.max(0, remaining));
          if (offset > 0) {
            await postJournal({
              date: issuedDate,
              description: `Advance applied — ${lineDesc}`,
              reference_type: 'package',
              reference_id: packageId,
              created_by: username,
              branch_id: selectedBranchId || null,
              lines: [
                { account_id: payable, debit_amount: offset, credit_amount: 0, description: `Advance applied — ${lineDesc}`, party_type: 'supplier', party_id: s.supplier_id },
                { account_id: ACCOUNTS.SUPPLIER_DEPOSITS, debit_amount: 0, credit_amount: offset, description: `Advance applied — ${lineDesc}`, party_type: 'supplier', party_id: s.supplier_id },
              ],
            });
          }
        }
      }

      // Auto-create ticket record. ticket_ref is NOT NULL — matches the ref pattern used
      // by the standalone Ticketing form ('TK' + last 7 digits of a timestamp). Legacy flat
      // airline/pnr/route columns mirror the first/last segment for backward compatibility.
      if (itemType === 'ticket') {
        const validSegments = segments.filter(s => s.airline).map(({ key, ...s }) => s);
        const { data: tk, error: tkErr } = await supabase.from('tickets').insert({
          ticket_ref: 'TK' + Date.now().toString().slice(-7),
          client_id: clientId,
          airline: validSegments[0]?.airline || null,
          pnr: validSegments[0]?.pnr || null,
          segments: validSegments,
          route_from: validSegments[0]?.route_from || null,
          route_to: validSegments[validSegments.length - 1]?.route_to || null,
          departure_date: travelDate || null,
          sell_price: totalAmount,
          sell_currency_id: currencyId || lkrId,
          invoice_id: inv.id,
          ticket_status: 'ACTIVE',
          created_by: username,
          branch_id: selectedBranchId || null,
        }).select('id').single();
        if (tkErr) throw tkErr;
        if (tk?.id) await supabase.from('invoices').update({ source_id: tk.id }).eq('id', inv.id);
      }

      // Auto-create visa record. visa_ref is NOT NULL — matches the ref pattern used by
      // the standalone Visa form ('VZ' + last 7 digits of a timestamp).
      if (itemType === 'visa') {
        // `visas` has no country column — country/type live on visa_types (matches the
        // catalog VisaForm.js picks from). Find a matching catalog row for what staff
        // typed here, or create one, so this free-text entry point stays consistent
        // with the standalone Visa form's data model.
        let visaTypeId = null;
        if (country || visaType) {
          const { data: existingVt } = await supabase.from('visa_types')
            .select('id').eq('country', country || '').eq('name', visaType || 'Visa').maybeSingle();
          if (existingVt?.id) {
            visaTypeId = existingVt.id;
          } else {
            const { data: newVt, error: vtErr } = await supabase.from('visa_types')
              .insert({ name: visaType || 'Visa', country: country || null, is_active: true }).select('id').single();
            if (vtErr) throw vtErr;
            visaTypeId = newVt.id;
          }
        }
        const { data: vs, error: vsErr } = await supabase.from('visas').insert({
          visa_ref: 'VZ' + Date.now().toString().slice(-7),
          client_id: clientId,
          visa_type_id: visaTypeId,
          visa_status: 'APPLIED',
          sell_price: totalAmount,
          sell_currency_id: currencyId || lkrId,
          invoice_id: inv.id,
          created_by: username,
          branch_id: selectedBranchId || null,
        }).select('id').single();
        if (vsErr) throw vsErr;
        if (vs?.id) await supabase.from('invoices').update({ source_id: vs.id }).eq('id', inv.id);
      }

      if (itemType === 'package' && bookingId) {
        await supabase.from('invoices').update({ source_id: bookingId }).eq('id', inv.id);
      }

      if (quotId) {
        await supabase.from('quotations').update({
          status: 'CONVERTED',
          converted_invoice_id: inv.id,
          updated_by: username,
        }).eq('id', quotId);
      }

      // Commission records — one agent can stack multiple rules (e.g. a flat per-unit
      // amount plus a separate GP-tiered cut) as separate rows.
      for (const line of commissionLines) {
        if (!(line.agent_id && line.include && parseFloat(line.commission_amount) > 0)) continue;
        const { error: commErr } = await supabase.from('commissions').insert({
          agent_ref_id: line.agent_id,
          reference_type: COMMISSION_REF_TYPE[itemType] || 'TICKET',
          reference_id: inv.id,
          product_type: commissionProductType,
          gp_amount: gpLKR,
          commission_percent: parseFloat(line.commission_percent) || null,
          commission_amount: parseFloat(line.commission_amount),
          currency_id: lkrId,
          commission_status: 'PENDING',
          commission_table_id: line.commission_table_id || null,
          units: parseFloat(line.units) || null,
        });
        if (commErr) throw commErr;
      }

      navigate(`/invoices/${inv.id}`);
    } catch (err) {
      setError(err.message || 'Error creating invoice');
      setSaving(false);
    }
  }

  const gpColor = gpLKR >= 0 ? '#16a34a' : '#dc2626';

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>
      {!branchConfirmed && (
        <BranchSelectModal
          defaultBranchId={selectedBranchId || branchId}
          onConfirm={(id) => { setSelectedBranchId(id); setBranchConfirmed(true); }}
          onCancel={() => navigate('/invoices')}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>New Invoice</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" style={{ minWidth: 140 }} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/invoices')}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : 'Create Invoice'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '10px 14px', background: 'rgba(220,38,38,0.08)', borderRadius: 7 }}>
          {error}
        </div>
      )}

      <div className="tabs" style={{ marginBottom: 0 }}>
        <div className={`tab ${tab === 'direct' ? 'active' : ''}`} onClick={() => switchTab('direct')}>Direct Invoice</div>
        <div className={`tab ${tab === 'quotation' ? 'active' : ''}`} onClick={() => switchTab('quotation')}>From Pro Forma</div>
      </div>

      <div className="card" style={{ borderTopLeftRadius: 0 }}>

        {tab === 'quotation' && (
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Pro Forma *</label>
            <TypeSearch
              fetchFn={async (q) => {
                let ids = [];
                if (q) {
                  const { data: mc } = await supabase.from('clients').select('id').ilike('full_name', `%${q}%`).limit(50);
                  ids = (mc || []).map(c => c.id);
                }
                let qb = supabase.from('quotations')
                  .select('id, quotation_number, item_type, client_id, amount_per_pax, num_pax, total_amount, currency_id, op_data, notes, clients(full_name), currency:currency_id(code)')
                  .neq('status', 'CONVERTED').limit(20);
                if (q) {
                  qb = ids.length > 0
                    ? qb.or(`quotation_number.ilike.%${q}%,client_id.in.(${ids.join(',')})`)
                    : qb.ilike('quotation_number', `%${q}%`);
                }
                const { data } = await qb;
                // Fetch deposits already paid against each pro forma (tagged via reference_type/reference_id,
                // not a quotation_id column) so staff can see what's already been collected before invoicing.
                const quotIds = (data || []).map(r => r.id);
                let paidMap = {};
                if (quotIds.length) {
                  const { data: receipts } = await supabase.from('receipt_vouchers')
                    .select('reference_id, amount, base_amount').eq('reference_type', 'quotation').in('reference_id', quotIds).is('voided_at', null);
                  (receipts || []).forEach(rv => {
                    paidMap[rv.reference_id] = (paidMap[rv.reference_id] || 0) + (parseFloat(rv.base_amount) || parseFloat(rv.amount) || 0);
                  });
                }
                return (data || []).map(r => {
                  const total = parseFloat(r.total_amount || 0);
                  const paid = paidMap[r.id] || 0;
                  const curr = r.currency?.code || 'LKR';
                  const amtStr = `${curr} ${total.toLocaleString()}`;
                  const paidStr = paid > 0 ? ` · Paid: ${curr} ${paid.toLocaleString()}` : '';
                  return { ...r, label: `${r.quotation_number} — ${r.clients?.full_name || ''} · ${amtStr}${paidStr}` };
                });
              }}
              value={quotLabel}
              displayKey="label"
              placeholder="Search pro forma number or client…"
              onSelect={fillFromQuotation}
              onClear={() => { setQuotId(''); setQuotLabel(''); resetAll(); setTab('quotation'); }}
            />
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 20 }}>
          <label className="form-label">Item Type *</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['ticket', 'visa', 'package'].map(t => (
              <button key={t}
                onClick={() => { if (tab === 'direct') { setItemType(t); clearItemFields(); } }}
                className={`btn btn-sm ${itemType === t ? 'btn-primary' : 'btn-secondary'}`}
                style={{ textTransform: 'capitalize', cursor: tab === 'quotation' ? 'default' : 'pointer', opacity: tab === 'quotation' && itemType !== t ? 0.5 : 1 }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 20 }}>
          <label className="form-label">Client *</label>
          {tab === 'quotation' && clientId ? (
            <div className="form-input" style={{ background: 'var(--bg-input)' }}>{clientLabel}</div>
          ) : (
            <TypeSearch
              fetchFn={async (q) => {
                const qAlt = q.includes('-') ? q.replace(/-/g, ' ') : q.replace(/ /g, '-');
                let orFilter = `full_name.ilike.%${q}%,client_code.ilike.%${q}%`;
                if (qAlt !== q) orFilter += `,full_name.ilike.%${qAlt}%,client_code.ilike.%${qAlt}%`;
                // also search by agent tls_code
                const agentOr = [...new Set([q, qAlt])].map(t => `tls_code.ilike.%${t}%`).join(',');
                const { data: ags } = await supabase.from('agents').select('id').or(agentOr);
                if (ags?.length) orFilter += `,care_of_agent_id.in.(${ags.map(a => a.id).join(',')})`;
                // Inactive (merged) clients are left out — invoicing one is how TLS-26-1334 landed on a dead record.
                const { data } = await supabase.from('clients').select('id, full_name, client_code, agents:care_of_agent_id(tls_code, full_name)').eq('is_active', true).or(orFilter).limit(30);
                return data || [];
              }}
              value={clientLabel}
              displayKey="client_code"
              placeholder="Search client…"
              onSelect={selectClient}
              onClear={() => { setClientId(''); setClientLabel(''); setDefaultAgentId(''); setDefaultAgentLabel(''); }}
              renderOption={(item, q) => {
                const hi = (t) => { if (!t) return t; const i = t.toLowerCase().indexOf(q.toLowerCase()); if (i === -1 || !q) return t; return <>{t.slice(0,i)}<strong style={{color:'var(--primary)'}}>{t.slice(i,i+q.length)}</strong>{t.slice(i+q.length)}</>; };
                const sub = [item.client_code, item.agents?.full_name || null].filter(Boolean).join(' · ');
                return (<><div style={{fontSize:13,fontWeight:500}}>{hi(item.full_name)}</div>{sub && <div style={{fontSize:11,color:'var(--text-muted)',marginTop:1}}>{sub}</div>}</>);
              }}
            />
          )}
        </div>

        {itemType === 'ticket' && (
          <div style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Travel Date</label>
              <input className="form-input" type="date" value={travelDate} onChange={e => setTravelDate(e.target.value)} style={{ maxWidth: 220 }} />
            </div>
            <label className="form-label">Flight Segments</label>
            {segments.map((seg, i) => (
              <div key={seg.key} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.8fr 0.8fr auto', gap: 10, marginBottom: 8, alignItems: 'center' }}>
                <TypeSearch
                  fetchFn={async (q) => {
                    if (!q || q.length < 2) return [];
                    const { data } = await supabase.from('airlines').select('iata_code, name').or(`name.ilike.%${q}%,iata_code.ilike.%${q}%`).eq('is_active', true).limit(12);
                    return (data || []).map(a => ({ ...a, id: a.iata_code, label: `${a.name} (${a.iata_code})` }));
                  }}
                  value={seg.airline}
                  displayKey="label"
                  placeholder={`Segment ${i + 1} airline — e.g. Emirates or EK`}
                  minChars={2}
                  onSelect={a => updateSegment(seg.key, 'airline', `${a.name} (${a.iata_code})`)}
                  onClear={() => updateSegment(seg.key, 'airline', '')}
                  renderOption={(a) => <><strong style={{ fontSize: 13 }}>{a.iata_code}</strong><span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-muted)' }}>{a.name}</span></>}
                />
                <input className="form-input" placeholder="PNR" value={seg.pnr} onChange={e => updateSegment(seg.key, 'pnr', e.target.value)} />
                <input className="form-input" placeholder="From (CMB)" value={seg.route_from} onChange={e => updateSegment(seg.key, 'route_from', e.target.value.toUpperCase())} />
                <input className="form-input" placeholder="To (DXB)" value={seg.route_to} onChange={e => updateSegment(seg.key, 'route_to', e.target.value.toUpperCase())} />
                {segments.length > 1 && (
                  <button type="button" onClick={() => removeSegment(seg.key)}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addSegment}>+ Add Segment</button>
          </div>
        )}

        {itemType === 'visa' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input className="form-input" placeholder="e.g. United Arab Emirates" value={country} onChange={e => setCountry(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Visa Type</label>
              <input className="form-input" placeholder="e.g. Tourist, Business" value={visaType} onChange={e => setVisaType(e.target.value)} />
            </div>
          </div>
        )}

        {itemType === 'package' && (
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Package Type *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['group', 'fit'].map(m => (
                <button key={m} type="button"
                  onClick={() => { setPackageMode(m); if (m === 'fit') { setPackageId(''); setPackageLabel(''); } }}
                  className={`btn btn-sm ${packageMode === m ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ textTransform: 'uppercase' }}>
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}

        {itemType === 'package' && packageMode === 'group' && (
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Package *</label>
            <TypeSearch
              fetchFn={async (q) => {
                const { data } = await supabase.from('packages')
                  .select('id, package_name, package_code')
                  .eq('package_type', 'Group').eq('is_active', true)
                  .ilike('package_name', `%${q}%`).limit(20);
                return (data || []).map(p => ({ ...p, label: `${p.package_name} (${p.package_code})` }));
              }}
              value={packageLabel}
              displayKey="label"
              onSelect={async p => {
                setPackageId(p.id); setPackageLabel(p.label);
                await pullPackageCosts(p.id);
              }}
              placeholder="Search Group package…"
              onClear={() => { setPackageId(''); setPackageLabel(''); }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Pulls this package's real supplier costs into Supplier Costs below — Group package invoices must be linked to a package, not hand-typed.
            </div>
          </div>
        )}

        {itemType === 'package' && (
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Booking (optional)</label>
            <TypeSearch
              fetchFn={async (q) => {
                // Only show bookings that don't already have an active invoice
                const { data: invoicedIds } = await supabase.from('invoices')
                  .select('source_id').neq('status', 'VOID').not('source_id', 'is', null);
                const usedIds = (invoicedIds || []).map(i => i.source_id);
                let qb = supabase.from('bookings')
                  .select('id, booking_ref, num_pax, total_sell, sell_currency_id, client_id, clients(full_name)')
                  .ilike('booking_ref', `%${q}%`).limit(20);
                if (usedIds.length) qb = qb.not('id', 'in', `(${usedIds.join(',')})`);
                const { data } = await qb;
                return (data || []).map(r => ({ ...r, label: `${r.booking_ref} — ${r.clients?.full_name || ''}` }));
              }}
              value={bookingLabel}
              displayKey="label"
              onSelect={b => {
                setBookingId(b.id);
                setBookingLabel(b.label);
                if (b.client_id) { setClientId(b.client_id); setClientLabel(b.clients?.full_name || ''); fetchAgentForClient(b.client_id); }
                if (b.total_sell) {
                  const pax = parseInt(b.num_pax) || 1;
                  setLineItems([{ type: 'Adult', rate: String(parseFloat(b.total_sell) / pax), qty: String(pax) }]);
                }
                if (b.sell_currency_id) setCurrencyId(b.sell_currency_id);
              }}
              placeholder="Search booking…"
              onClear={() => { setBookingId(''); setBookingLabel(''); }}
            />
          </div>
        )}

        {/* Line Items */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.7fr 1fr 0.7fr 1fr auto', gap: 8, marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pax Type</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Passenger Name</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rate</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Qty</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Subtotal</div>
            <div />
          </div>
          {lineItems.map((li, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.7fr 1fr 0.7fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              {li.type === 'Other' || !PAX_TYPES.includes(li.type) && li.type !== '' ? (
                <input className="form-input" placeholder="Describe type…" autoFocus
                  value={PAX_TYPES.includes(li.type) ? '' : li.type === 'Other' ? '' : li.type}
                  onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, type: e.target.value } : r))}
                  onBlur={e => { if (!e.target.value) setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, type: 'Adult' } : r)); }} />
              ) : (
                <select className="form-input" value={li.type}
                  onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, type: e.target.value } : r))}>
                  {PAX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  <option value="Other">Other…</option>
                </select>
              )}
              {/* Passenger name lives in its own field — typing it into Pax Type made per-type
                  package costs (e.g. "AIR ASIA (Adult)") match nobody, so they never posted. */}
              <input className="form-input" placeholder="Optional"
                value={li.name || ''} onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, name: e.target.value } : r))} />
              <input className="form-input" type="number" placeholder="0.00" min="0" step="0.01"
                value={li.rate} onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, rate: e.target.value } : r))} />
              <input className="form-input" type="number" placeholder="1" min="1"
                value={li.qty} onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, qty: e.target.value } : r))} />
              <div className="form-input" style={{ background: 'var(--bg-input)', fontWeight: 600, fontSize: 13 }}>
                {((parseFloat(li.rate) || 0) * (parseInt(li.qty) || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setLineItems(ls => ls.filter((_, idx) => idx !== i))}
                disabled={lineItems.length === 1}>✕</button>
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setLineItems(ls => [...ls, { type: 'Adult', rate: '', qty: '1' }])}>+ Add Row</button>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Total: {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="form-group">
            <label className="form-label">Currency *</label>
            <select className="form-input" value={currencyId} onChange={e => setCurrencyId(e.target.value)}>
              <option value="">— Select —</option>
              {currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Issued Date *</label>
            <input className="form-input" type="date" value={issuedDate} onChange={e => setIssuedDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input className="form-input" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
        </div>



        <div className="form-group" style={{ marginBottom: 24 }}>
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
        </div>

        {/* Supplier Costs */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showCost ? 12 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Supplier Costs</div>
              {!showCost && <button className="btn btn-secondary btn-sm" onClick={() => setShowCost(true)}>+ Add Cost</button>}
            </div>
            {showCost && <button className="btn btn-secondary btn-sm" onClick={addSupplier}>+ Add Supplier</button>}
          </div>
          {showCost && suppliers.map((s, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                  <input type="radio" checked={(s.cost_type || 'supplier') === 'supplier'}
                    onChange={() => updateSupplier(i, { cost_type: 'supplier', supplier_label: '' })} /> Supplier
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                  <input type="radio" checked={s.cost_type === 'internal'}
                    onChange={() => updateSupplier(i, { cost_type: 'internal', supplier_id: '', supplier_label: '' })} /> Internal / Tour Operator Cost
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: lineItems.length > 1 ? '1.6fr 0.8fr 0.7fr 0.7fr 1.3fr auto auto' : '2fr 1fr 1fr 1fr auto auto', gap: 10, alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">{s.cost_type === 'internal' ? 'Description' : 'Supplier'}</label>}
                  {s.cost_type === 'internal' ? (
                    <input className="form-input" placeholder="e.g. Guide allowance — Kasun" value={s.supplier_label}
                      onChange={e => updateSupplier(i, { supplier_label: e.target.value })} />
                  ) : (
                    <SupplierPicker row={s} onChange={patch => updateSupplier(i, patch)} />
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">Cost Amount</label>}
                  <input className="form-input" type="number" placeholder="0.00" value={s.cost_amount}
                    onChange={e => updateSupplier(i, { cost_amount: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">Currency</label>}
                  <select className="form-input" value={s.currency_id || lkrId}
                    onChange={e => updateSupplier(i, { currency_id: e.target.value })}>
                    {currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">Ex. Rate</label>}
                  <input className="form-input" type="number" placeholder="1" value={s.exchange_rate}
                    onChange={e => updateSupplier(i, { exchange_rate: e.target.value })} />
                </div>
                {lineItems.length > 1 && (
                  <div className="form-group" style={{ margin: 0 }}>
                    {i === 0 && <label className="form-label">Applies To</label>}
                    <select className="form-input" value={s.pax_type_filter ? `type:${s.pax_type_filter}` : (s.line_item_index ?? '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (v.startsWith('type:')) updateSupplier(i, { pax_type_filter: v.slice(5), line_item_index: '' });
                        else updateSupplier(i, { line_item_index: v, pax_type_filter: '' });
                      }}>
                      <option value="">Shared (Split Evenly)</option>
                      {PAX_TYPES.map(t => <option key={t} value={`type:${t}`}>Every "{t}" line</option>)}
                      {lineItems.map((item, idx) => (
                        <option key={idx} value={idx}>{item.name ? `${item.name} (${item.type})` : paxLabel(item.type, idx)}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  {i === 0 && <label className="form-label" style={{ whiteSpace: 'nowrap' }}>Per Pax</label>}
                  <input type="checkbox" checked={!!s.per_pax}
                    onChange={e => updateSupplier(i, { per_pax: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer', marginTop: i === 0 ? 0 : 10 }} />
                </div>
                <button className="btn btn-secondary btn-sm" style={{ marginTop: i === 0 ? 18 : 0 }}
                  onClick={() => removeSupplier(i)}>✕</button>
              </div>
              <input className="form-input" placeholder="Notes (e.g. Adult x2, Child fare…)" value={s.notes || ''}
                style={{ marginTop: 6, fontSize: 12 }}
                onChange={e => updateSupplier(i, { notes: e.target.value })} />
              {s.cost_type === 'internal' && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Posts to Other Payables (2060), not a supplier — and won't hit the books until this invoice's costing is finalized via Costing Pending.
                </div>
              )}
            </div>
          ))}
        </div>

        {/* GP Summary — only shown when cost section is open */}
        {showCost && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
          {perPassengerMargins.length > 1 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>GP Per Passenger</div>
              <table style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                    <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 4 }}>Passenger</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Sell</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Margin</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Commission</th>
                    <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>GP</th>
                  </tr>
                </thead>
                <tbody>
                  {perPassengerMargins.map((p, i) => {
                    const comm = perPaxCommission[i] || 0;
                    const netGP = p.margin - comm;
                    return (
                      <tr key={i}>
                        <td style={{ padding: '2px 8px 2px 0', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11.5 }} title={p.description}>{p.description || `Passenger ${i + 1}`}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(p.sell)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(p.margin)}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{comm > 0 ? `(${fmt(comm)})` : fmt(0)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: netGP >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(netGP)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', padding: '12px 16px', background: 'var(--bg-subtle, #f9fafb)', borderRadius: 8, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Total Sell (Cumulative)</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>LKR {fmt(totalAmount)}</div>
            </div>
            <div style={{ color: 'var(--text-muted)' }}>−</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Total Cost (Cumulative)</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>LKR {fmt(totalCostLKR)}</div>
            </div>
            {totalCommission > 0 && (
              <>
                <div style={{ color: 'var(--text-muted)' }}>−</div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Commission</div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>LKR {fmt(totalCommission)}</div>
                </div>
              </>
            )}
            <div style={{ color: 'var(--text-muted)' }}>=</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
                {totalCommission > 0 ? 'Net GP (Cumulative)' : 'Gross Profit (Cumulative)'}
              </div>
              {(() => {
                const netGP = gpLKR - totalCommission;
                const netPct = totalAmount > 0 ? (netGP / totalAmount * 100) : 0;
                const col = netGP >= 0 ? '#16a34a' : '#dc2626';
                return (
                  <div style={{ fontSize: 15, fontWeight: 700, color: col }}>
                    LKR {fmt(netGP)} <span style={{ fontSize: 12, fontWeight: 500 }}>({netPct.toFixed(1)}%)</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        )}

        {/* Commission — one agent can stack more than one rule (e.g. a flat per-visa
            amount plus a separate GP-tiered cut), so this is a repeatable list. */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Commission</div>
            <button className="btn btn-secondary btn-sm" onClick={addCommissionLine}>+ Add Commission</button>
          </div>
          {commissionLines.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No commission lines yet.</div>
          )}
          {commissionLines.map((line, i) => (
            <div key={line.key} style={{ marginBottom: 12, paddingBottom: i < commissionLines.length - 1 ? 12 : 0, borderBottom: i < commissionLines.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.7fr 1fr auto auto', gap: 12, alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">Agent</label>}
                  <TypeSearch
                    fetchFn={async (q) => {
                      const { data } = await supabase.from('agents').select('id, tls_code, full_name').or(`full_name.ilike.%${q}%,tls_code.ilike.%${q}%`).limit(20);
                      return (data || []).map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` }));
                    }}
                    value={line.agent_label}
                    displayKey="label"
                    placeholder="Search agent…"
                    onSelect={a => updateCommissionLine(line.key, { agent_id: a.id, agent_label: `${a.tls_code} — ${a.full_name}` })}
                    onClear={() => updateCommissionLine(line.key, { agent_id: '', agent_label: '', commission_amount: '', commission_percent: '', commission_table_id: '' })}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">Rule</label>}
                  {availableRuleNames.length > 0 ? (
                    <select className="form-input" value={line.rule_name} onChange={e => updateCommissionLine(line.key, { rule_name: e.target.value })}>
                      <option value="">— Select rule —</option>
                      {availableRuleNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  ) : (
                    <div className="form-input" style={{ background: 'var(--bg-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
                      {line.commission_table_id
                        ? (line.commission_percent ? `${line.commission_percent}% of GP` : 'Fixed amount')
                        : line.agent_id ? 'No matching rule' : '—'}
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label" title="How many visas/tickets/units this agent covers — matters for per-unit rules, and lets you split one invoice across several agents">Units</label>}
                  <input className="form-input" type="number" min="0" value={line.units}
                    onChange={e => updateCommissionLine(line.key, { units: e.target.value })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  {i === 0 && <label className="form-label">Commission (LKR)</label>}
                  <input className="form-input" type="number" value={line.commission_amount}
                    onChange={e => !line.commission_table_id && updateCommissionLine(line.key, { commission_amount: e.target.value })}
                    placeholder="0.00"
                    readOnly={!!line.commission_table_id}
                    style={line.commission_table_id ? { background: 'var(--bg-secondary, #f5f5f5)', color: 'var(--text-muted)', cursor: 'default' } : {}} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: i === 0 ? 6 : 0, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={line.include} onChange={e => updateCommissionLine(line.key, { include: e.target.checked })} />
                  Include
                </label>
                <button className="btn btn-secondary btn-sm" onClick={() => removeCommissionLine(line.key)}>x</button>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

function buildLineItemsForSave(type, { lineItems, totalAmount, segments, travelDate, country, visaType, bookingLabel }) {
  const validSegments = (segments || []).filter(s => s.airline);
  const sub = type === 'ticket' ? [
    ...validSegments.map(s => `${s.airline}${s.pnr ? ` (${s.pnr})` : ''}: ${s.route_from} → ${s.route_to}`),
    travelDate ? `Travel: ${travelDate}` : null,
  ].filter(Boolean).join('\n') : null;

  const baseDesc = type === 'ticket'
    ? `Flight Ticket${validSegments[0]?.airline ? ` — ${validSegments[0].airline}` : ''}`
    : type === 'visa'
    ? `Visa Processing${country ? ` — ${country}` : ''}${visaType ? ` (${visaType})` : ''}`
    : bookingLabel || 'Travel Package';

  return lineItems
    .filter(l => parseFloat(l.rate) > 0 && parseInt(l.qty) > 0)
    .map(l => ({
      description: `${(l.name || '').trim() || l.type} — ${baseDesc}`,
      type: l.type,
      sub: sub || null,
      qty: parseInt(l.qty) || 1,
      unitPrice: parseFloat(l.rate) || 0,
      amount: (parseFloat(l.rate) || 0) * (parseInt(l.qty) || 1),
    }));
}
